'use client';
import { isPlainObject as isPlainObject$1, nanoid, createSelector as createSelector$1, createAction, createAsyncThunk, SHOULD_AUTOBATCH, createSlice, prepareAutoBatched, isAnyOf, isFulfilled, isRejectedWithValue, combineReducers, formatProdErrorMessage, isAllOf, isPending, isRejected, createNextState, isAction, isAsyncThunkAction } from '@reduxjs/toolkit';
import { useCallback, useMemo, useDebugValue, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { batch, useDispatch, useSelector, useStore, shallowEqual } from 'react-redux';
import 'lodash';
import 'react-router-dom';

// src/utils/env.ts
var NOTHING = Symbol.for("immer-nothing");
var DRAFTABLE = Symbol.for("immer-draftable");
var DRAFT_STATE = Symbol.for("immer-state");

// src/utils/errors.ts
var errors = process.env.NODE_ENV !== "production" ? [
  // All error codes, starting by 0:
  function(plugin) {
    return `The plugin for '${plugin}' has not been loaded into Immer. To enable the plugin, import and call \`enable${plugin}()\` when initializing your application.`;
  },
  function(thing) {
    return `produce can only be called on things that are draftable: plain objects, arrays, Map, Set or classes that are marked with '[immerable]: true'. Got '${thing}'`;
  },
  "This object has been frozen and should not be mutated",
  function(data) {
    return "Cannot use a proxy that has been revoked. Did you pass an object from inside an immer function to an async process? " + data;
  },
  "An immer producer returned a new value *and* modified its draft. Either return a new value *or* modify the draft.",
  "Immer forbids circular references",
  "The first or second argument to `produce` must be a function",
  "The third argument to `produce` must be a function or undefined",
  "First argument to `createDraft` must be a plain object, an array, or an immerable object",
  "First argument to `finishDraft` must be a draft returned by `createDraft`",
  function(thing) {
    return `'current' expects a draft, got: ${thing}`;
  },
  "Object.defineProperty() cannot be used on an Immer draft",
  "Object.setPrototypeOf() cannot be used on an Immer draft",
  "Immer only supports deleting array indices",
  "Immer only supports setting array indices and the 'length' property",
  function(thing) {
    return `'original' expects a draft, got: ${thing}`;
  }
  // Note: if more errors are added, the errorOffset in Patches.ts should be increased
  // See Patches.ts for additional errors
] : [];
function die(error, ...args) {
  if (process.env.NODE_ENV !== "production") {
    const e = errors[error];
    const msg = typeof e === "function" ? e.apply(null, args) : e;
    throw new Error(`[Immer] ${msg}`);
  }
  throw new Error(
    `[Immer] minified error nr: ${error}. Full error at: https://bit.ly/3cXEKWf`
  );
}

// src/utils/common.ts
var getPrototypeOf = Object.getPrototypeOf;
function isDraft(value) {
  return !!value && !!value[DRAFT_STATE];
}
function isDraftable(value) {
  if (!value)
    return false;
  return isPlainObject(value) || Array.isArray(value) || !!value[DRAFTABLE] || !!value.constructor?.[DRAFTABLE] || isMap(value) || isSet(value);
}
var objectCtorString = Object.prototype.constructor.toString();
function isPlainObject(value) {
  if (!value || typeof value !== "object")
    return false;
  const proto = getPrototypeOf(value);
  if (proto === null) {
    return true;
  }
  const Ctor = Object.hasOwnProperty.call(proto, "constructor") && proto.constructor;
  if (Ctor === Object)
    return true;
  return typeof Ctor == "function" && Function.toString.call(Ctor) === objectCtorString;
}
function original(value) {
  if (!isDraft(value))
    die(15, value);
  return value[DRAFT_STATE].base_;
}
function each(obj, iter) {
  if (getArchtype(obj) === 0 /* Object */) {
    Reflect.ownKeys(obj).forEach((key) => {
      iter(key, obj[key], obj);
    });
  } else {
    obj.forEach((entry, index) => iter(index, entry, obj));
  }
}
function getArchtype(thing) {
  const state = thing[DRAFT_STATE];
  return state ? state.type_ : Array.isArray(thing) ? 1 /* Array */ : isMap(thing) ? 2 /* Map */ : isSet(thing) ? 3 /* Set */ : 0 /* Object */;
}
function has(thing, prop) {
  return getArchtype(thing) === 2 /* Map */ ? thing.has(prop) : Object.prototype.hasOwnProperty.call(thing, prop);
}
function get(thing, prop) {
  return getArchtype(thing) === 2 /* Map */ ? thing.get(prop) : thing[prop];
}
function set(thing, propOrOldValue, value) {
  const t = getArchtype(thing);
  if (t === 2 /* Map */)
    thing.set(propOrOldValue, value);
  else if (t === 3 /* Set */) {
    thing.add(value);
  } else
    thing[propOrOldValue] = value;
}
function is(x, y) {
  if (x === y) {
    return x !== 0 || 1 / x === 1 / y;
  } else {
    return x !== x && y !== y;
  }
}
function isMap(target) {
  return target instanceof Map;
}
function isSet(target) {
  return target instanceof Set;
}
function latest(state) {
  return state.copy_ || state.base_;
}
function shallowCopy(base, strict) {
  if (isMap(base)) {
    return new Map(base);
  }
  if (isSet(base)) {
    return new Set(base);
  }
  if (Array.isArray(base))
    return Array.prototype.slice.call(base);
  const isPlain = isPlainObject(base);
  if (strict === true || strict === "class_only" && !isPlain) {
    const descriptors = Object.getOwnPropertyDescriptors(base);
    delete descriptors[DRAFT_STATE];
    let keys = Reflect.ownKeys(descriptors);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const desc = descriptors[key];
      if (desc.writable === false) {
        desc.writable = true;
        desc.configurable = true;
      }
      if (desc.get || desc.set)
        descriptors[key] = {
          configurable: true,
          writable: true,
          // could live with !!desc.set as well here...
          enumerable: desc.enumerable,
          value: base[key]
        };
    }
    return Object.create(getPrototypeOf(base), descriptors);
  } else {
    const proto = getPrototypeOf(base);
    if (proto !== null && isPlain) {
      return { ...base };
    }
    const obj = Object.create(proto);
    return Object.assign(obj, base);
  }
}
function freeze(obj, deep = false) {
  if (isFrozen(obj) || isDraft(obj) || !isDraftable(obj))
    return obj;
  if (getArchtype(obj) > 1) {
    obj.set = obj.add = obj.clear = obj.delete = dontMutateFrozenCollections;
  }
  Object.freeze(obj);
  if (deep)
    Object.entries(obj).forEach(([key, value]) => freeze(value, true));
  return obj;
}
function dontMutateFrozenCollections() {
  die(2);
}
function isFrozen(obj) {
  return Object.isFrozen(obj);
}

// src/utils/plugins.ts
var plugins = {};
function getPlugin(pluginKey) {
  const plugin = plugins[pluginKey];
  if (!plugin) {
    die(0, pluginKey);
  }
  return plugin;
}
function loadPlugin(pluginKey, implementation) {
  if (!plugins[pluginKey])
    plugins[pluginKey] = implementation;
}

// src/core/scope.ts
var currentScope;
function getCurrentScope() {
  return currentScope;
}
function createScope(parent_, immer_) {
  return {
    drafts_: [],
    parent_,
    immer_,
    // Whenever the modified draft contains a draft from another scope, we
    // need to prevent auto-freezing so the unowned draft can be finalized.
    canAutoFreeze_: true,
    unfinalizedDrafts_: 0
  };
}
function usePatchesInScope(scope, patchListener) {
  if (patchListener) {
    getPlugin("Patches");
    scope.patches_ = [];
    scope.inversePatches_ = [];
    scope.patchListener_ = patchListener;
  }
}
function revokeScope(scope) {
  leaveScope(scope);
  scope.drafts_.forEach(revokeDraft);
  scope.drafts_ = null;
}
function leaveScope(scope) {
  if (scope === currentScope) {
    currentScope = scope.parent_;
  }
}
function enterScope(immer2) {
  return currentScope = createScope(currentScope, immer2);
}
function revokeDraft(draft) {
  const state = draft[DRAFT_STATE];
  if (state.type_ === 0 /* Object */ || state.type_ === 1 /* Array */)
    state.revoke_();
  else
    state.revoked_ = true;
}

// src/core/finalize.ts
function processResult(result, scope) {
  scope.unfinalizedDrafts_ = scope.drafts_.length;
  const baseDraft = scope.drafts_[0];
  const isReplaced = result !== void 0 && result !== baseDraft;
  if (isReplaced) {
    if (baseDraft[DRAFT_STATE].modified_) {
      revokeScope(scope);
      die(4);
    }
    if (isDraftable(result)) {
      result = finalize(scope, result);
      if (!scope.parent_)
        maybeFreeze(scope, result);
    }
    if (scope.patches_) {
      getPlugin("Patches").generateReplacementPatches_(
        baseDraft[DRAFT_STATE].base_,
        result,
        scope.patches_,
        scope.inversePatches_
      );
    }
  } else {
    result = finalize(scope, baseDraft, []);
  }
  revokeScope(scope);
  if (scope.patches_) {
    scope.patchListener_(scope.patches_, scope.inversePatches_);
  }
  return result !== NOTHING ? result : void 0;
}
function finalize(rootScope, value, path) {
  if (isFrozen(value))
    return value;
  const state = value[DRAFT_STATE];
  if (!state) {
    each(
      value,
      (key, childValue) => finalizeProperty(rootScope, state, value, key, childValue, path)
    );
    return value;
  }
  if (state.scope_ !== rootScope)
    return value;
  if (!state.modified_) {
    maybeFreeze(rootScope, state.base_, true);
    return state.base_;
  }
  if (!state.finalized_) {
    state.finalized_ = true;
    state.scope_.unfinalizedDrafts_--;
    const result = state.copy_;
    let resultEach = result;
    let isSet2 = false;
    if (state.type_ === 3 /* Set */) {
      resultEach = new Set(result);
      result.clear();
      isSet2 = true;
    }
    each(
      resultEach,
      (key, childValue) => finalizeProperty(rootScope, state, result, key, childValue, path, isSet2)
    );
    maybeFreeze(rootScope, result, false);
    if (path && rootScope.patches_) {
      getPlugin("Patches").generatePatches_(
        state,
        path,
        rootScope.patches_,
        rootScope.inversePatches_
      );
    }
  }
  return state.copy_;
}
function finalizeProperty(rootScope, parentState, targetObject, prop, childValue, rootPath, targetIsSet) {
  if (process.env.NODE_ENV !== "production" && childValue === targetObject)
    die(5);
  if (isDraft(childValue)) {
    const path = rootPath && parentState && parentState.type_ !== 3 /* Set */ && // Set objects are atomic since they have no keys.
    !has(parentState.assigned_, prop) ? rootPath.concat(prop) : void 0;
    const res = finalize(rootScope, childValue, path);
    set(targetObject, prop, res);
    if (isDraft(res)) {
      rootScope.canAutoFreeze_ = false;
    } else
      return;
  } else if (targetIsSet) {
    targetObject.add(childValue);
  }
  if (isDraftable(childValue) && !isFrozen(childValue)) {
    if (!rootScope.immer_.autoFreeze_ && rootScope.unfinalizedDrafts_ < 1) {
      return;
    }
    finalize(rootScope, childValue);
    if ((!parentState || !parentState.scope_.parent_) && typeof prop !== "symbol" && Object.prototype.propertyIsEnumerable.call(targetObject, prop))
      maybeFreeze(rootScope, childValue);
  }
}
function maybeFreeze(scope, value, deep = false) {
  if (!scope.parent_ && scope.immer_.autoFreeze_ && scope.canAutoFreeze_) {
    freeze(value, deep);
  }
}

// src/core/proxy.ts
function createProxyProxy(base, parent) {
  const isArray = Array.isArray(base);
  const state = {
    type_: isArray ? 1 /* Array */ : 0 /* Object */,
    // Track which produce call this is associated with.
    scope_: parent ? parent.scope_ : getCurrentScope(),
    // True for both shallow and deep changes.
    modified_: false,
    // Used during finalization.
    finalized_: false,
    // Track which properties have been assigned (true) or deleted (false).
    assigned_: {},
    // The parent draft state.
    parent_: parent,
    // The base state.
    base_: base,
    // The base proxy.
    draft_: null,
    // set below
    // The base copy with any updated values.
    copy_: null,
    // Called by the `produce` function.
    revoke_: null,
    isManual_: false
  };
  let target = state;
  let traps = objectTraps;
  if (isArray) {
    target = [state];
    traps = arrayTraps;
  }
  const { revoke, proxy } = Proxy.revocable(target, traps);
  state.draft_ = proxy;
  state.revoke_ = revoke;
  return proxy;
}
var objectTraps = {
  get(state, prop) {
    if (prop === DRAFT_STATE)
      return state;
    const source = latest(state);
    if (!has(source, prop)) {
      return readPropFromProto(state, source, prop);
    }
    const value = source[prop];
    if (state.finalized_ || !isDraftable(value)) {
      return value;
    }
    if (value === peek(state.base_, prop)) {
      prepareCopy(state);
      return state.copy_[prop] = createProxy(value, state);
    }
    return value;
  },
  has(state, prop) {
    return prop in latest(state);
  },
  ownKeys(state) {
    return Reflect.ownKeys(latest(state));
  },
  set(state, prop, value) {
    const desc = getDescriptorFromProto(latest(state), prop);
    if (desc?.set) {
      desc.set.call(state.draft_, value);
      return true;
    }
    if (!state.modified_) {
      const current2 = peek(latest(state), prop);
      const currentState = current2?.[DRAFT_STATE];
      if (currentState && currentState.base_ === value) {
        state.copy_[prop] = value;
        state.assigned_[prop] = false;
        return true;
      }
      if (is(value, current2) && (value !== void 0 || has(state.base_, prop)))
        return true;
      prepareCopy(state);
      markChanged(state);
    }
    if (state.copy_[prop] === value && // special case: handle new props with value 'undefined'
    (value !== void 0 || prop in state.copy_) || // special case: NaN
    Number.isNaN(value) && Number.isNaN(state.copy_[prop]))
      return true;
    state.copy_[prop] = value;
    state.assigned_[prop] = true;
    return true;
  },
  deleteProperty(state, prop) {
    if (peek(state.base_, prop) !== void 0 || prop in state.base_) {
      state.assigned_[prop] = false;
      prepareCopy(state);
      markChanged(state);
    } else {
      delete state.assigned_[prop];
    }
    if (state.copy_) {
      delete state.copy_[prop];
    }
    return true;
  },
  // Note: We never coerce `desc.value` into an Immer draft, because we can't make
  // the same guarantee in ES5 mode.
  getOwnPropertyDescriptor(state, prop) {
    const owner = latest(state);
    const desc = Reflect.getOwnPropertyDescriptor(owner, prop);
    if (!desc)
      return desc;
    return {
      writable: true,
      configurable: state.type_ !== 1 /* Array */ || prop !== "length",
      enumerable: desc.enumerable,
      value: owner[prop]
    };
  },
  defineProperty() {
    die(11);
  },
  getPrototypeOf(state) {
    return getPrototypeOf(state.base_);
  },
  setPrototypeOf() {
    die(12);
  }
};
var arrayTraps = {};
each(objectTraps, (key, fn) => {
  arrayTraps[key] = function() {
    arguments[0] = arguments[0][0];
    return fn.apply(this, arguments);
  };
});
arrayTraps.deleteProperty = function(state, prop) {
  if (process.env.NODE_ENV !== "production" && isNaN(parseInt(prop)))
    die(13);
  return arrayTraps.set.call(this, state, prop, void 0);
};
arrayTraps.set = function(state, prop, value) {
  if (process.env.NODE_ENV !== "production" && prop !== "length" && isNaN(parseInt(prop)))
    die(14);
  return objectTraps.set.call(this, state[0], prop, value, state[0]);
};
function peek(draft, prop) {
  const state = draft[DRAFT_STATE];
  const source = state ? latest(state) : draft;
  return source[prop];
}
function readPropFromProto(state, source, prop) {
  const desc = getDescriptorFromProto(source, prop);
  return desc ? `value` in desc ? desc.value : (
    // This is a very special case, if the prop is a getter defined by the
    // prototype, we should invoke it with the draft as context!
    desc.get?.call(state.draft_)
  ) : void 0;
}
function getDescriptorFromProto(source, prop) {
  if (!(prop in source))
    return void 0;
  let proto = getPrototypeOf(source);
  while (proto) {
    const desc = Object.getOwnPropertyDescriptor(proto, prop);
    if (desc)
      return desc;
    proto = getPrototypeOf(proto);
  }
  return void 0;
}
function markChanged(state) {
  if (!state.modified_) {
    state.modified_ = true;
    if (state.parent_) {
      markChanged(state.parent_);
    }
  }
}
function prepareCopy(state) {
  if (!state.copy_) {
    state.copy_ = shallowCopy(
      state.base_,
      state.scope_.immer_.useStrictShallowCopy_
    );
  }
}

// src/core/immerClass.ts
var Immer2 = class {
  constructor(config) {
    this.autoFreeze_ = true;
    this.useStrictShallowCopy_ = false;
    /**
     * The `produce` function takes a value and a "recipe function" (whose
     * return value often depends on the base state). The recipe function is
     * free to mutate its first argument however it wants. All mutations are
     * only ever applied to a __copy__ of the base state.
     *
     * Pass only a function to create a "curried producer" which relieves you
     * from passing the recipe function every time.
     *
     * Only plain objects and arrays are made mutable. All other objects are
     * considered uncopyable.
     *
     * Note: This function is __bound__ to its `Immer` instance.
     *
     * @param {any} base - the initial state
     * @param {Function} recipe - function that receives a proxy of the base state as first argument and which can be freely modified
     * @param {Function} patchListener - optional function that will be called with all the patches produced here
     * @returns {any} a new state, or the initial state if nothing was modified
     */
    this.produce = (base, recipe, patchListener) => {
      if (typeof base === "function" && typeof recipe !== "function") {
        const defaultBase = recipe;
        recipe = base;
        const self = this;
        return function curriedProduce(base2 = defaultBase, ...args) {
          return self.produce(base2, (draft) => recipe.call(this, draft, ...args));
        };
      }
      if (typeof recipe !== "function")
        die(6);
      if (patchListener !== void 0 && typeof patchListener !== "function")
        die(7);
      let result;
      if (isDraftable(base)) {
        const scope = enterScope(this);
        const proxy = createProxy(base, void 0);
        let hasError = true;
        try {
          result = recipe(proxy);
          hasError = false;
        } finally {
          if (hasError)
            revokeScope(scope);
          else
            leaveScope(scope);
        }
        usePatchesInScope(scope, patchListener);
        return processResult(result, scope);
      } else if (!base || typeof base !== "object") {
        result = recipe(base);
        if (result === void 0)
          result = base;
        if (result === NOTHING)
          result = void 0;
        if (this.autoFreeze_)
          freeze(result, true);
        if (patchListener) {
          const p = [];
          const ip = [];
          getPlugin("Patches").generateReplacementPatches_(base, result, p, ip);
          patchListener(p, ip);
        }
        return result;
      } else
        die(1, base);
    };
    this.produceWithPatches = (base, recipe) => {
      if (typeof base === "function") {
        return (state, ...args) => this.produceWithPatches(state, (draft) => base(draft, ...args));
      }
      let patches, inversePatches;
      const result = this.produce(base, recipe, (p, ip) => {
        patches = p;
        inversePatches = ip;
      });
      return [result, patches, inversePatches];
    };
    if (typeof config?.autoFreeze === "boolean")
      this.setAutoFreeze(config.autoFreeze);
    if (typeof config?.useStrictShallowCopy === "boolean")
      this.setUseStrictShallowCopy(config.useStrictShallowCopy);
  }
  createDraft(base) {
    if (!isDraftable(base))
      die(8);
    if (isDraft(base))
      base = current(base);
    const scope = enterScope(this);
    const proxy = createProxy(base, void 0);
    proxy[DRAFT_STATE].isManual_ = true;
    leaveScope(scope);
    return proxy;
  }
  finishDraft(draft, patchListener) {
    const state = draft && draft[DRAFT_STATE];
    if (!state || !state.isManual_)
      die(9);
    const { scope_: scope } = state;
    usePatchesInScope(scope, patchListener);
    return processResult(void 0, scope);
  }
  /**
   * Pass true to automatically freeze all copies created by Immer.
   *
   * By default, auto-freezing is enabled.
   */
  setAutoFreeze(value) {
    this.autoFreeze_ = value;
  }
  /**
   * Pass true to enable strict shallow copy.
   *
   * By default, immer does not copy the object descriptors such as getter, setter and non-enumrable properties.
   */
  setUseStrictShallowCopy(value) {
    this.useStrictShallowCopy_ = value;
  }
  applyPatches(base, patches) {
    let i;
    for (i = patches.length - 1; i >= 0; i--) {
      const patch = patches[i];
      if (patch.path.length === 0 && patch.op === "replace") {
        base = patch.value;
        break;
      }
    }
    if (i > -1) {
      patches = patches.slice(i + 1);
    }
    const applyPatchesImpl = getPlugin("Patches").applyPatches_;
    if (isDraft(base)) {
      return applyPatchesImpl(base, patches);
    }
    return this.produce(
      base,
      (draft) => applyPatchesImpl(draft, patches)
    );
  }
};
function createProxy(value, parent) {
  const draft = isMap(value) ? getPlugin("MapSet").proxyMap_(value, parent) : isSet(value) ? getPlugin("MapSet").proxySet_(value, parent) : createProxyProxy(value, parent);
  const scope = parent ? parent.scope_ : getCurrentScope();
  scope.drafts_.push(draft);
  return draft;
}

// src/core/current.ts
function current(value) {
  if (!isDraft(value))
    die(10, value);
  return currentImpl(value);
}
function currentImpl(value) {
  if (!isDraftable(value) || isFrozen(value))
    return value;
  const state = value[DRAFT_STATE];
  let copy;
  if (state) {
    if (!state.modified_)
      return state.base_;
    state.finalized_ = true;
    copy = shallowCopy(value, state.scope_.immer_.useStrictShallowCopy_);
  } else {
    copy = shallowCopy(value, true);
  }
  each(copy, (key, childValue) => {
    set(copy, key, currentImpl(childValue));
  });
  if (state) {
    state.finalized_ = false;
  }
  return copy;
}

// src/plugins/patches.ts
function enablePatches() {
  const errorOffset = 16;
  if (process.env.NODE_ENV !== "production") {
    errors.push(
      'Sets cannot have "replace" patches.',
      function(op) {
        return "Unsupported patch operation: " + op;
      },
      function(path) {
        return "Cannot apply patch, path doesn't resolve: " + path;
      },
      "Patching reserved attributes like __proto__, prototype and constructor is not allowed"
    );
  }
  const REPLACE = "replace";
  const ADD = "add";
  const REMOVE = "remove";
  function generatePatches_(state, basePath, patches, inversePatches) {
    switch (state.type_) {
      case 0 /* Object */:
      case 2 /* Map */:
        return generatePatchesFromAssigned(
          state,
          basePath,
          patches,
          inversePatches
        );
      case 1 /* Array */:
        return generateArrayPatches(state, basePath, patches, inversePatches);
      case 3 /* Set */:
        return generateSetPatches(
          state,
          basePath,
          patches,
          inversePatches
        );
    }
  }
  function generateArrayPatches(state, basePath, patches, inversePatches) {
    let { base_, assigned_ } = state;
    let copy_ = state.copy_;
    if (copy_.length < base_.length) {
      [base_, copy_] = [copy_, base_];
      [patches, inversePatches] = [inversePatches, patches];
    }
    for (let i = 0; i < base_.length; i++) {
      if (assigned_[i] && copy_[i] !== base_[i]) {
        const path = basePath.concat([i]);
        patches.push({
          op: REPLACE,
          path,
          // Need to maybe clone it, as it can in fact be the original value
          // due to the base/copy inversion at the start of this function
          value: clonePatchValueIfNeeded(copy_[i])
        });
        inversePatches.push({
          op: REPLACE,
          path,
          value: clonePatchValueIfNeeded(base_[i])
        });
      }
    }
    for (let i = base_.length; i < copy_.length; i++) {
      const path = basePath.concat([i]);
      patches.push({
        op: ADD,
        path,
        // Need to maybe clone it, as it can in fact be the original value
        // due to the base/copy inversion at the start of this function
        value: clonePatchValueIfNeeded(copy_[i])
      });
    }
    for (let i = copy_.length - 1; base_.length <= i; --i) {
      const path = basePath.concat([i]);
      inversePatches.push({
        op: REMOVE,
        path
      });
    }
  }
  function generatePatchesFromAssigned(state, basePath, patches, inversePatches) {
    const { base_, copy_ } = state;
    each(state.assigned_, (key, assignedValue) => {
      const origValue = get(base_, key);
      const value = get(copy_, key);
      const op = !assignedValue ? REMOVE : has(base_, key) ? REPLACE : ADD;
      if (origValue === value && op === REPLACE)
        return;
      const path = basePath.concat(key);
      patches.push(op === REMOVE ? { op, path } : { op, path, value });
      inversePatches.push(
        op === ADD ? { op: REMOVE, path } : op === REMOVE ? { op: ADD, path, value: clonePatchValueIfNeeded(origValue) } : { op: REPLACE, path, value: clonePatchValueIfNeeded(origValue) }
      );
    });
  }
  function generateSetPatches(state, basePath, patches, inversePatches) {
    let { base_, copy_ } = state;
    let i = 0;
    base_.forEach((value) => {
      if (!copy_.has(value)) {
        const path = basePath.concat([i]);
        patches.push({
          op: REMOVE,
          path,
          value
        });
        inversePatches.unshift({
          op: ADD,
          path,
          value
        });
      }
      i++;
    });
    i = 0;
    copy_.forEach((value) => {
      if (!base_.has(value)) {
        const path = basePath.concat([i]);
        patches.push({
          op: ADD,
          path,
          value
        });
        inversePatches.unshift({
          op: REMOVE,
          path,
          value
        });
      }
      i++;
    });
  }
  function generateReplacementPatches_(baseValue, replacement, patches, inversePatches) {
    patches.push({
      op: REPLACE,
      path: [],
      value: replacement === NOTHING ? void 0 : replacement
    });
    inversePatches.push({
      op: REPLACE,
      path: [],
      value: baseValue
    });
  }
  function applyPatches_(draft, patches) {
    patches.forEach((patch) => {
      const { path, op } = patch;
      let base = draft;
      for (let i = 0; i < path.length - 1; i++) {
        const parentType = getArchtype(base);
        let p = path[i];
        if (typeof p !== "string" && typeof p !== "number") {
          p = "" + p;
        }
        if ((parentType === 0 /* Object */ || parentType === 1 /* Array */) && (p === "__proto__" || p === "constructor"))
          die(errorOffset + 3);
        if (typeof base === "function" && p === "prototype")
          die(errorOffset + 3);
        base = get(base, p);
        if (typeof base !== "object")
          die(errorOffset + 2, path.join("/"));
      }
      const type = getArchtype(base);
      const value = deepClonePatchValue(patch.value);
      const key = path[path.length - 1];
      switch (op) {
        case REPLACE:
          switch (type) {
            case 2 /* Map */:
              return base.set(key, value);
            case 3 /* Set */:
              die(errorOffset);
            default:
              return base[key] = value;
          }
        case ADD:
          switch (type) {
            case 1 /* Array */:
              return key === "-" ? base.push(value) : base.splice(key, 0, value);
            case 2 /* Map */:
              return base.set(key, value);
            case 3 /* Set */:
              return base.add(value);
            default:
              return base[key] = value;
          }
        case REMOVE:
          switch (type) {
            case 1 /* Array */:
              return base.splice(key, 1);
            case 2 /* Map */:
              return base.delete(key);
            case 3 /* Set */:
              return base.delete(patch.value);
            default:
              return delete base[key];
          }
        default:
          die(errorOffset + 1, op);
      }
    });
    return draft;
  }
  function deepClonePatchValue(obj) {
    if (!isDraftable(obj))
      return obj;
    if (Array.isArray(obj))
      return obj.map(deepClonePatchValue);
    if (isMap(obj))
      return new Map(
        Array.from(obj.entries()).map(([k, v]) => [k, deepClonePatchValue(v)])
      );
    if (isSet(obj))
      return new Set(Array.from(obj).map(deepClonePatchValue));
    const cloned = Object.create(getPrototypeOf(obj));
    for (const key in obj)
      cloned[key] = deepClonePatchValue(obj[key]);
    if (has(obj, DRAFTABLE))
      cloned[DRAFTABLE] = obj[DRAFTABLE];
    return cloned;
  }
  function clonePatchValueIfNeeded(obj) {
    if (isDraft(obj)) {
      return deepClonePatchValue(obj);
    } else
      return obj;
  }
  loadPlugin("Patches", {
    applyPatches_,
    generatePatches_,
    generateReplacementPatches_
  });
}

// src/immer.ts
var immer = new Immer2();
immer.produce;
var produceWithPatches = immer.produceWithPatches.bind(
  immer
);
immer.setAutoFreeze.bind(immer);
immer.setUseStrictShallowCopy.bind(immer);
var applyPatches = immer.applyPatches.bind(immer);
immer.createDraft.bind(immer);
immer.finishDraft.bind(immer);

// src/devModeChecks/identityFunctionCheck.ts
var runIdentityFunctionCheck = (resultFunc, inputSelectorsResults, outputSelectorResult) => {
  if (inputSelectorsResults.length === 1 && inputSelectorsResults[0] === outputSelectorResult) {
    let isInputSameAsOutput = false;
    try {
      const emptyObject = {};
      if (resultFunc(emptyObject) === emptyObject)
        isInputSameAsOutput = true;
    } catch {
    }
    if (isInputSameAsOutput) {
      let stack = void 0;
      try {
        throw new Error();
      } catch (e) {
        ({ stack } = e);
      }
      console.warn(
        "The result function returned its own inputs without modification. e.g\n`createSelector([state => state.todos], todos => todos)`\nThis could lead to inefficient memoization and unnecessary re-renders.\nEnsure transformation logic is in the result function, and extraction logic is in the input selectors.",
        { stack }
      );
    }
  }
};

// src/devModeChecks/inputStabilityCheck.ts
var runInputStabilityCheck = (inputSelectorResultsObject, options, inputSelectorArgs) => {
  const { memoize, memoizeOptions } = options;
  const { inputSelectorResults, inputSelectorResultsCopy } = inputSelectorResultsObject;
  const createAnEmptyObject = memoize(() => ({}), ...memoizeOptions);
  const areInputSelectorResultsEqual = createAnEmptyObject.apply(null, inputSelectorResults) === createAnEmptyObject.apply(null, inputSelectorResultsCopy);
  if (!areInputSelectorResultsEqual) {
    let stack = void 0;
    try {
      throw new Error();
    } catch (e) {
      ({ stack } = e);
    }
    console.warn(
      "An input selector returned a different result when passed same arguments.\nThis means your output selector will likely run more frequently than intended.\nAvoid returning a new reference inside your input selector, e.g.\n`createSelector([state => state.todos.map(todo => todo.id)], todoIds => todoIds.length)`",
      {
        arguments: inputSelectorArgs,
        firstInputs: inputSelectorResults,
        secondInputs: inputSelectorResultsCopy,
        stack
      }
    );
  }
};

// src/devModeChecks/setGlobalDevModeChecks.ts
var globalDevModeChecks = {
  inputStabilityCheck: "once",
  identityFunctionCheck: "once"
};
function assertIsFunction(func, errorMessage = `expected a function, instead received ${typeof func}`) {
  if (typeof func !== "function") {
    throw new TypeError(errorMessage);
  }
}
function assertIsObject(object, errorMessage = `expected an object, instead received ${typeof object}`) {
  if (typeof object !== "object") {
    throw new TypeError(errorMessage);
  }
}
function assertIsArrayOfFunctions(array, errorMessage = `expected all items to be functions, instead received the following types: `) {
  if (!array.every((item) => typeof item === "function")) {
    const itemTypes = array.map(
      (item) => typeof item === "function" ? `function ${item.name || "unnamed"}()` : typeof item
    ).join(", ");
    throw new TypeError(`${errorMessage}[${itemTypes}]`);
  }
}
var ensureIsArray = (item) => {
  return Array.isArray(item) ? item : [item];
};
function getDependencies(createSelectorArgs) {
  const dependencies = Array.isArray(createSelectorArgs[0]) ? createSelectorArgs[0] : createSelectorArgs;
  assertIsArrayOfFunctions(
    dependencies,
    `createSelector expects all input-selectors to be functions, but received the following types: `
  );
  return dependencies;
}
function collectInputSelectorResults(dependencies, inputSelectorArgs) {
  const inputSelectorResults = [];
  const { length } = dependencies;
  for (let i = 0; i < length; i++) {
    inputSelectorResults.push(dependencies[i].apply(null, inputSelectorArgs));
  }
  return inputSelectorResults;
}
var getDevModeChecksExecutionInfo = (firstRun, devModeChecks) => {
  const { identityFunctionCheck, inputStabilityCheck } = {
    ...globalDevModeChecks,
    ...devModeChecks
  };
  return {
    identityFunctionCheck: {
      shouldRun: identityFunctionCheck === "always" || identityFunctionCheck === "once" && firstRun,
      run: runIdentityFunctionCheck
    },
    inputStabilityCheck: {
      shouldRun: inputStabilityCheck === "always" || inputStabilityCheck === "once" && firstRun,
      run: runInputStabilityCheck
    }
  };
};

// src/weakMapMemoize.ts
var StrongRef = class {
  constructor(value) {
    this.value = value;
  }
  deref() {
    return this.value;
  }
};
var Ref = typeof WeakRef !== "undefined" ? WeakRef : StrongRef;
var UNTERMINATED = 0;
var TERMINATED = 1;
function createCacheNode() {
  return {
    s: UNTERMINATED,
    v: void 0,
    o: null,
    p: null
  };
}
function weakMapMemoize(func, options = {}) {
  let fnNode = createCacheNode();
  const { resultEqualityCheck } = options;
  let lastResult;
  let resultsCount = 0;
  function memoized() {
    let cacheNode = fnNode;
    const { length } = arguments;
    for (let i = 0, l = length; i < l; i++) {
      const arg = arguments[i];
      if (typeof arg === "function" || typeof arg === "object" && arg !== null) {
        let objectCache = cacheNode.o;
        if (objectCache === null) {
          cacheNode.o = objectCache = /* @__PURE__ */ new WeakMap();
        }
        const objectNode = objectCache.get(arg);
        if (objectNode === void 0) {
          cacheNode = createCacheNode();
          objectCache.set(arg, cacheNode);
        } else {
          cacheNode = objectNode;
        }
      } else {
        let primitiveCache = cacheNode.p;
        if (primitiveCache === null) {
          cacheNode.p = primitiveCache = /* @__PURE__ */ new Map();
        }
        const primitiveNode = primitiveCache.get(arg);
        if (primitiveNode === void 0) {
          cacheNode = createCacheNode();
          primitiveCache.set(arg, cacheNode);
        } else {
          cacheNode = primitiveNode;
        }
      }
    }
    const terminatedNode = cacheNode;
    let result;
    if (cacheNode.s === TERMINATED) {
      result = cacheNode.v;
    } else {
      result = func.apply(null, arguments);
      resultsCount++;
    }
    terminatedNode.s = TERMINATED;
    if (resultEqualityCheck) {
      const lastResultValue = lastResult?.deref?.() ?? lastResult;
      if (lastResultValue != null && resultEqualityCheck(lastResultValue, result)) {
        result = lastResultValue;
        resultsCount !== 0 && resultsCount--;
      }
      const needsWeakRef = typeof result === "object" && result !== null || typeof result === "function";
      lastResult = needsWeakRef ? new Ref(result) : result;
    }
    terminatedNode.v = result;
    return result;
  }
  memoized.clearCache = () => {
    fnNode = createCacheNode();
    memoized.resetResultsCount();
  };
  memoized.resultsCount = () => resultsCount;
  memoized.resetResultsCount = () => {
    resultsCount = 0;
  };
  return memoized;
}

// src/createSelectorCreator.ts
function createSelectorCreator(memoizeOrOptions, ...memoizeOptionsFromArgs) {
  const createSelectorCreatorOptions = typeof memoizeOrOptions === "function" ? {
    memoize: memoizeOrOptions,
    memoizeOptions: memoizeOptionsFromArgs
  } : memoizeOrOptions;
  const createSelector2 = (...createSelectorArgs) => {
    let recomputations = 0;
    let dependencyRecomputations = 0;
    let lastResult;
    let directlyPassedOptions = {};
    let resultFunc = createSelectorArgs.pop();
    if (typeof resultFunc === "object") {
      directlyPassedOptions = resultFunc;
      resultFunc = createSelectorArgs.pop();
    }
    assertIsFunction(
      resultFunc,
      `createSelector expects an output function after the inputs, but received: [${typeof resultFunc}]`
    );
    const combinedOptions = {
      ...createSelectorCreatorOptions,
      ...directlyPassedOptions
    };
    const {
      memoize,
      memoizeOptions = [],
      argsMemoize = weakMapMemoize,
      argsMemoizeOptions = [],
      devModeChecks = {}
    } = combinedOptions;
    const finalMemoizeOptions = ensureIsArray(memoizeOptions);
    const finalArgsMemoizeOptions = ensureIsArray(argsMemoizeOptions);
    const dependencies = getDependencies(createSelectorArgs);
    const memoizedResultFunc = memoize(function recomputationWrapper() {
      recomputations++;
      return resultFunc.apply(
        null,
        arguments
      );
    }, ...finalMemoizeOptions);
    let firstRun = true;
    const selector = argsMemoize(function dependenciesChecker() {
      dependencyRecomputations++;
      const inputSelectorResults = collectInputSelectorResults(
        dependencies,
        arguments
      );
      lastResult = memoizedResultFunc.apply(null, inputSelectorResults);
      if (process.env.NODE_ENV !== "production") {
        const { identityFunctionCheck, inputStabilityCheck } = getDevModeChecksExecutionInfo(firstRun, devModeChecks);
        if (identityFunctionCheck.shouldRun) {
          identityFunctionCheck.run(
            resultFunc,
            inputSelectorResults,
            lastResult
          );
        }
        if (inputStabilityCheck.shouldRun) {
          const inputSelectorResultsCopy = collectInputSelectorResults(
            dependencies,
            arguments
          );
          inputStabilityCheck.run(
            { inputSelectorResults, inputSelectorResultsCopy },
            { memoize, memoizeOptions: finalMemoizeOptions },
            arguments
          );
        }
        if (firstRun)
          firstRun = false;
      }
      return lastResult;
    }, ...finalArgsMemoizeOptions);
    return Object.assign(selector, {
      resultFunc,
      memoizedResultFunc,
      dependencies,
      dependencyRecomputations: () => dependencyRecomputations,
      resetDependencyRecomputations: () => {
        dependencyRecomputations = 0;
      },
      lastResult: () => lastResult,
      recomputations: () => recomputations,
      resetRecomputations: () => {
        recomputations = 0;
      },
      memoize,
      argsMemoize
    });
  };
  Object.assign(createSelector2, {
    withTypes: () => createSelector2
  });
  return createSelector2;
}
var createSelector = /* @__PURE__ */ createSelectorCreator(weakMapMemoize);

// src/createStructuredSelector.ts
var createStructuredSelector = Object.assign(
  (inputSelectorsObject, selectorCreator = createSelector) => {
    assertIsObject(
      inputSelectorsObject,
      `createStructuredSelector expects first argument to be an object where each property is a selector, instead received a ${typeof inputSelectorsObject}`
    );
    const inputSelectorKeys = Object.keys(inputSelectorsObject);
    const dependencies = inputSelectorKeys.map(
      (key) => inputSelectorsObject[key]
    );
    const structuredSelector = selectorCreator(
      dependencies,
      (...inputSelectorResults) => {
        return inputSelectorResults.reduce((composition, value, index) => {
          composition[inputSelectorKeys[index]] = value;
          return composition;
        }, {});
      }
    );
    return structuredSelector;
  },
  { withTypes: () => createStructuredSelector }
);

// src/query/core/apiState.ts
var QueryStatus = /* @__PURE__ */ ((QueryStatus2) => {
  QueryStatus2["uninitialized"] = "uninitialized";
  QueryStatus2["pending"] = "pending";
  QueryStatus2["fulfilled"] = "fulfilled";
  QueryStatus2["rejected"] = "rejected";
  return QueryStatus2;
})(QueryStatus || {});
function getRequestStatusFlags(status) {
  return {
    status,
    isUninitialized: status === "uninitialized" /* uninitialized */,
    isLoading: status === "pending" /* pending */,
    isSuccess: status === "fulfilled" /* fulfilled */,
    isError: status === "rejected" /* rejected */
  };
}

// src/query/utils/isAbsoluteUrl.ts
function isAbsoluteUrl(url) {
  return new RegExp(`(^|:)//`).test(url);
}

// src/query/utils/joinUrls.ts
var withoutTrailingSlash = (url) => url.replace(/\/$/, "");
var withoutLeadingSlash = (url) => url.replace(/^\//, "");
function joinUrls(base, url) {
  if (!base) {
    return url;
  }
  if (!url) {
    return base;
  }
  if (isAbsoluteUrl(url)) {
    return url;
  }
  const delimiter = base.endsWith("/") || !url.startsWith("?") ? "/" : "";
  base = withoutTrailingSlash(base);
  url = withoutLeadingSlash(url);
  return `${base}${delimiter}${url}`;
}

// src/query/utils/flatten.ts
var flatten = (arr) => [].concat(...arr);

// src/query/utils/isOnline.ts
function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine === void 0 ? true : navigator.onLine;
}

// src/query/utils/isDocumentVisible.ts
function isDocumentVisible() {
  if (typeof document === "undefined") {
    return true;
  }
  return document.visibilityState !== "hidden";
}

// src/query/utils/copyWithStructuralSharing.ts
var isPlainObject2 = isPlainObject$1;
function copyWithStructuralSharing(oldObj, newObj) {
  if (oldObj === newObj || !(isPlainObject2(oldObj) && isPlainObject2(newObj) || Array.isArray(oldObj) && Array.isArray(newObj))) {
    return newObj;
  }
  const newKeys = Object.keys(newObj);
  const oldKeys = Object.keys(oldObj);
  let isSameObject = newKeys.length === oldKeys.length;
  const mergeObj = Array.isArray(newObj) ? [] : {};
  for (const key of newKeys) {
    mergeObj[key] = copyWithStructuralSharing(oldObj[key], newObj[key]);
    if (isSameObject)
      isSameObject = oldObj[key] === mergeObj[key];
  }
  return isSameObject ? oldObj : mergeObj;
}

// src/query/fetchBaseQuery.ts
var defaultFetchFn = (...args) => fetch(...args);
var defaultValidateStatus = (response) => response.status >= 200 && response.status <= 299;
var defaultIsJsonContentType = (headers) => (
  /*applicat*/
  /ion\/(vnd\.api\+)?json/.test(headers.get("content-type") || "")
);
function stripUndefined(obj) {
  if (!isPlainObject$1(obj)) {
    return obj;
  }
  const copy = {
    ...obj
  };
  for (const [k, v] of Object.entries(copy)) {
    if (v === void 0)
      delete copy[k];
  }
  return copy;
}
function fetchBaseQuery({
  baseUrl,
  prepareHeaders = (x) => x,
  fetchFn = defaultFetchFn,
  paramsSerializer,
  isJsonContentType = defaultIsJsonContentType,
  jsonContentType = "application/json",
  jsonReplacer,
  timeout: defaultTimeout,
  responseHandler: globalResponseHandler,
  validateStatus: globalValidateStatus,
  ...baseFetchOptions
} = {}) {
  if (typeof fetch === "undefined" && fetchFn === defaultFetchFn) {
    console.warn("Warning: `fetch` is not available. Please supply a custom `fetchFn` property to use `fetchBaseQuery` on SSR environments.");
  }
  return async (arg, api) => {
    const {
      signal,
      getState,
      extra,
      endpoint,
      forced,
      type
    } = api;
    let meta;
    let {
      url,
      headers = new Headers(baseFetchOptions.headers),
      params = void 0,
      responseHandler = globalResponseHandler ?? "json",
      validateStatus = globalValidateStatus ?? defaultValidateStatus,
      timeout = defaultTimeout,
      ...rest
    } = typeof arg == "string" ? {
      url: arg
    } : arg;
    let config = {
      ...baseFetchOptions,
      signal,
      ...rest
    };
    headers = new Headers(stripUndefined(headers));
    config.headers = await prepareHeaders(headers, {
      getState,
      extra,
      endpoint,
      forced,
      type
    }) || headers;
    const isJsonifiable = (body) => typeof body === "object" && (isPlainObject$1(body) || Array.isArray(body) || typeof body.toJSON === "function");
    if (!config.headers.has("content-type") && isJsonifiable(config.body)) {
      config.headers.set("content-type", jsonContentType);
    }
    if (isJsonifiable(config.body) && isJsonContentType(config.headers)) {
      config.body = JSON.stringify(config.body, jsonReplacer);
    }
    if (params) {
      const divider = ~url.indexOf("?") ? "&" : "?";
      const query = paramsSerializer ? paramsSerializer(params) : new URLSearchParams(stripUndefined(params));
      url += divider + query;
    }
    url = joinUrls(baseUrl, url);
    const request = new Request(url, config);
    const requestClone = new Request(url, config);
    meta = {
      request: requestClone
    };
    let response, timedOut = false, timeoutId = timeout && setTimeout(() => {
      timedOut = true;
      api.abort();
    }, timeout);
    try {
      response = await fetchFn(request);
    } catch (e) {
      return {
        error: {
          status: timedOut ? "TIMEOUT_ERROR" : "FETCH_ERROR",
          error: String(e)
        },
        meta
      };
    } finally {
      if (timeoutId)
        clearTimeout(timeoutId);
    }
    const responseClone = response.clone();
    meta.response = responseClone;
    let resultData;
    let responseText = "";
    try {
      let handleResponseError;
      await Promise.all([
        handleResponse(response, responseHandler).then((r) => resultData = r, (e) => handleResponseError = e),
        // see https://github.com/node-fetch/node-fetch/issues/665#issuecomment-538995182
        // we *have* to "use up" both streams at the same time or they will stop running in node-fetch scenarios
        responseClone.text().then((r) => responseText = r, () => {
        })
      ]);
      if (handleResponseError)
        throw handleResponseError;
    } catch (e) {
      return {
        error: {
          status: "PARSING_ERROR",
          originalStatus: response.status,
          data: responseText,
          error: String(e)
        },
        meta
      };
    }
    return validateStatus(response, resultData) ? {
      data: resultData,
      meta
    } : {
      error: {
        status: response.status,
        data: resultData
      },
      meta
    };
  };
  async function handleResponse(response, responseHandler) {
    if (typeof responseHandler === "function") {
      return responseHandler(response);
    }
    if (responseHandler === "content-type") {
      responseHandler = isJsonContentType(response.headers) ? "json" : "text";
    }
    if (responseHandler === "json") {
      const text = await response.text();
      return text.length ? JSON.parse(text) : null;
    }
    return response.text();
  }
}

// src/query/HandledError.ts
var HandledError = class {
  constructor(value, meta = void 0) {
    this.value = value;
    this.meta = meta;
  }
};

// src/query/core/setupListeners.ts
var onFocus = /* @__PURE__ */ createAction("__rtkq/focused");
var onFocusLost = /* @__PURE__ */ createAction("__rtkq/unfocused");
var onOnline = /* @__PURE__ */ createAction("__rtkq/online");
var onOffline = /* @__PURE__ */ createAction("__rtkq/offline");

// src/query/endpointDefinitions.ts
function isQueryDefinition$1(e) {
  return e.type === "query" /* query */;
}
function isMutationDefinition$1(e) {
  return e.type === "mutation" /* mutation */;
}
function calculateProvidedBy(description, result, error, queryArg, meta, assertTagTypes) {
  if (isFunction(description)) {
    return description(result, error, queryArg, meta).map(expandTagDescription).map(assertTagTypes);
  }
  if (Array.isArray(description)) {
    return description.map(expandTagDescription).map(assertTagTypes);
  }
  return [];
}
function isFunction(t) {
  return typeof t === "function";
}
function expandTagDescription(description) {
  return typeof description === "string" ? {
    type: description
  } : description;
}

// src/query/utils/isNotNullish.ts
function isNotNullish(v) {
  return v != null;
}

// src/query/utils/countObjectKeys.ts
function countObjectKeys$1(obj) {
  let count = 0;
  for (const _key in obj) {
    count++;
  }
  return count;
}

// src/tsHelpers.ts
function asSafePromise(promise, fallback) {
  return promise.catch(fallback);
}

// src/query/core/buildInitiate.ts
var forceQueryFnSymbol = Symbol("forceQueryFn");
var isUpsertQuery = (arg) => typeof arg[forceQueryFnSymbol] === "function";
function buildInitiate({
  serializeQueryArgs,
  queryThunk,
  mutationThunk,
  api,
  context
}) {
  const runningQueries = /* @__PURE__ */ new Map();
  const runningMutations = /* @__PURE__ */ new Map();
  const {
    unsubscribeQueryResult,
    removeMutationResult,
    updateSubscriptionOptions
  } = api.internalActions;
  return {
    buildInitiateQuery,
    buildInitiateMutation,
    getRunningQueryThunk,
    getRunningMutationThunk,
    getRunningQueriesThunk,
    getRunningMutationsThunk
  };
  function getRunningQueryThunk(endpointName, queryArgs) {
    return (dispatch) => {
      const endpointDefinition = context.endpointDefinitions[endpointName];
      const queryCacheKey = serializeQueryArgs({
        queryArgs,
        endpointDefinition,
        endpointName
      });
      return runningQueries.get(dispatch)?.[queryCacheKey];
    };
  }
  function getRunningMutationThunk(_endpointName, fixedCacheKeyOrRequestId) {
    return (dispatch) => {
      return runningMutations.get(dispatch)?.[fixedCacheKeyOrRequestId];
    };
  }
  function getRunningQueriesThunk() {
    return (dispatch) => Object.values(runningQueries.get(dispatch) || {}).filter(isNotNullish);
  }
  function getRunningMutationsThunk() {
    return (dispatch) => Object.values(runningMutations.get(dispatch) || {}).filter(isNotNullish);
  }
  function middlewareWarning(dispatch) {
    if (process.env.NODE_ENV !== "production") {
      if (middlewareWarning.triggered)
        return;
      const returnedValue = dispatch(api.internalActions.internal_getRTKQSubscriptions());
      middlewareWarning.triggered = true;
      if (typeof returnedValue !== "object" || typeof returnedValue?.type === "string") {
        throw new Error(process.env.NODE_ENV === "production" ? formatProdErrorMessage(34) : `Warning: Middleware for RTK-Query API at reducerPath "${api.reducerPath}" has not been added to the store.
You must add the middleware for RTK-Query to function correctly!`);
      }
    }
  }
  function buildInitiateQuery(endpointName, endpointDefinition) {
    const queryAction = (arg, {
      subscribe = true,
      forceRefetch,
      subscriptionOptions,
      [forceQueryFnSymbol]: forceQueryFn,
      ...rest
    } = {}) => (dispatch, getState) => {
      const queryCacheKey = serializeQueryArgs({
        queryArgs: arg,
        endpointDefinition,
        endpointName
      });
      const thunk = queryThunk({
        ...rest,
        type: "query",
        subscribe,
        forceRefetch,
        subscriptionOptions,
        endpointName,
        originalArgs: arg,
        queryCacheKey,
        [forceQueryFnSymbol]: forceQueryFn
      });
      const selector = api.endpoints[endpointName].select(arg);
      const thunkResult = dispatch(thunk);
      const stateAfter = selector(getState());
      middlewareWarning(dispatch);
      const {
        requestId,
        abort
      } = thunkResult;
      const skippedSynchronously = stateAfter.requestId !== requestId;
      const runningQuery = runningQueries.get(dispatch)?.[queryCacheKey];
      const selectFromState = () => selector(getState());
      const statePromise = Object.assign(forceQueryFn ? (
        // a query has been forced (upsertQueryData)
        // -> we want to resolve it once data has been written with the data that will be written
        thunkResult.then(selectFromState)
      ) : skippedSynchronously && !runningQuery ? (
        // a query has been skipped due to a condition and we do not have any currently running query
        // -> we want to resolve it immediately with the current data
        Promise.resolve(stateAfter)
      ) : (
        // query just started or one is already in flight
        // -> wait for the running query, then resolve with data from after that
        Promise.all([runningQuery, thunkResult]).then(selectFromState)
      ), {
        arg,
        requestId,
        subscriptionOptions,
        queryCacheKey,
        abort,
        async unwrap() {
          const result = await statePromise;
          if (result.isError) {
            throw result.error;
          }
          return result.data;
        },
        refetch: () => dispatch(queryAction(arg, {
          subscribe: false,
          forceRefetch: true
        })),
        unsubscribe() {
          if (subscribe)
            dispatch(unsubscribeQueryResult({
              queryCacheKey,
              requestId
            }));
        },
        updateSubscriptionOptions(options) {
          statePromise.subscriptionOptions = options;
          dispatch(updateSubscriptionOptions({
            endpointName,
            requestId,
            queryCacheKey,
            options
          }));
        }
      });
      if (!runningQuery && !skippedSynchronously && !forceQueryFn) {
        const running = runningQueries.get(dispatch) || {};
        running[queryCacheKey] = statePromise;
        runningQueries.set(dispatch, running);
        statePromise.then(() => {
          delete running[queryCacheKey];
          if (!countObjectKeys$1(running)) {
            runningQueries.delete(dispatch);
          }
        });
      }
      return statePromise;
    };
    return queryAction;
  }
  function buildInitiateMutation(endpointName) {
    return (arg, {
      track = true,
      fixedCacheKey
    } = {}) => (dispatch, getState) => {
      const thunk = mutationThunk({
        type: "mutation",
        endpointName,
        originalArgs: arg,
        track,
        fixedCacheKey
      });
      const thunkResult = dispatch(thunk);
      middlewareWarning(dispatch);
      const {
        requestId,
        abort,
        unwrap
      } = thunkResult;
      const returnValuePromise = asSafePromise(thunkResult.unwrap().then((data) => ({
        data
      })), (error) => ({
        error
      }));
      const reset = () => {
        dispatch(removeMutationResult({
          requestId,
          fixedCacheKey
        }));
      };
      const ret = Object.assign(returnValuePromise, {
        arg: thunkResult.arg,
        requestId,
        abort,
        unwrap,
        reset
      });
      const running = runningMutations.get(dispatch) || {};
      runningMutations.set(dispatch, running);
      running[requestId] = ret;
      ret.then(() => {
        delete running[requestId];
        if (!countObjectKeys$1(running)) {
          runningMutations.delete(dispatch);
        }
      });
      if (fixedCacheKey) {
        running[fixedCacheKey] = ret;
        ret.then(() => {
          if (running[fixedCacheKey] === ret) {
            delete running[fixedCacheKey];
            if (!countObjectKeys$1(running)) {
              runningMutations.delete(dispatch);
            }
          }
        });
      }
      return ret;
    };
  }
}
function defaultTransformResponse(baseQueryReturnValue) {
  return baseQueryReturnValue;
}
function buildThunks({
  reducerPath,
  baseQuery,
  context: {
    endpointDefinitions
  },
  serializeQueryArgs,
  api,
  assertTagType
}) {
  const patchQueryData = (endpointName, args, patches, updateProvided) => (dispatch, getState) => {
    const endpointDefinition = endpointDefinitions[endpointName];
    const queryCacheKey = serializeQueryArgs({
      queryArgs: args,
      endpointDefinition,
      endpointName
    });
    dispatch(api.internalActions.queryResultPatched({
      queryCacheKey,
      patches
    }));
    if (!updateProvided) {
      return;
    }
    const newValue = api.endpoints[endpointName].select(args)(
      // Work around TS 4.1 mismatch
      getState()
    );
    const providedTags = calculateProvidedBy(endpointDefinition.providesTags, newValue.data, void 0, args, {}, assertTagType);
    dispatch(api.internalActions.updateProvidedBy({
      queryCacheKey,
      providedTags
    }));
  };
  const updateQueryData = (endpointName, args, updateRecipe, updateProvided = true) => (dispatch, getState) => {
    const endpointDefinition = api.endpoints[endpointName];
    const currentState = endpointDefinition.select(args)(
      // Work around TS 4.1 mismatch
      getState()
    );
    let ret = {
      patches: [],
      inversePatches: [],
      undo: () => dispatch(api.util.patchQueryData(endpointName, args, ret.inversePatches, updateProvided))
    };
    if (currentState.status === "uninitialized" /* uninitialized */) {
      return ret;
    }
    let newValue;
    if ("data" in currentState) {
      if (isDraftable(currentState.data)) {
        const [value, patches, inversePatches] = produceWithPatches(currentState.data, updateRecipe);
        ret.patches.push(...patches);
        ret.inversePatches.push(...inversePatches);
        newValue = value;
      } else {
        newValue = updateRecipe(currentState.data);
        ret.patches.push({
          op: "replace",
          path: [],
          value: newValue
        });
        ret.inversePatches.push({
          op: "replace",
          path: [],
          value: currentState.data
        });
      }
    }
    if (ret.patches.length === 0) {
      return ret;
    }
    dispatch(api.util.patchQueryData(endpointName, args, ret.patches, updateProvided));
    return ret;
  };
  const upsertQueryData = (endpointName, args, value) => (dispatch) => {
    return dispatch(api.endpoints[endpointName].initiate(args, {
      subscribe: false,
      forceRefetch: true,
      [forceQueryFnSymbol]: () => ({
        data: value
      })
    }));
  };
  const executeEndpoint = async (arg, {
    signal,
    abort,
    rejectWithValue,
    fulfillWithValue,
    dispatch,
    getState,
    extra
  }) => {
    const endpointDefinition = endpointDefinitions[arg.endpointName];
    try {
      let transformResponse = defaultTransformResponse;
      let result;
      const baseQueryApi = {
        signal,
        abort,
        dispatch,
        getState,
        extra,
        endpoint: arg.endpointName,
        type: arg.type,
        forced: arg.type === "query" ? isForcedQuery(arg, getState()) : void 0
      };
      const forceQueryFn = arg.type === "query" ? arg[forceQueryFnSymbol] : void 0;
      if (forceQueryFn) {
        result = forceQueryFn();
      } else if (endpointDefinition.query) {
        result = await baseQuery(endpointDefinition.query(arg.originalArgs), baseQueryApi, endpointDefinition.extraOptions);
        if (endpointDefinition.transformResponse) {
          transformResponse = endpointDefinition.transformResponse;
        }
      } else {
        result = await endpointDefinition.queryFn(arg.originalArgs, baseQueryApi, endpointDefinition.extraOptions, (arg2) => baseQuery(arg2, baseQueryApi, endpointDefinition.extraOptions));
      }
      if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
        const what = endpointDefinition.query ? "`baseQuery`" : "`queryFn`";
        let err;
        if (!result) {
          err = `${what} did not return anything.`;
        } else if (typeof result !== "object") {
          err = `${what} did not return an object.`;
        } else if (result.error && result.data) {
          err = `${what} returned an object containing both \`error\` and \`result\`.`;
        } else if (result.error === void 0 && result.data === void 0) {
          err = `${what} returned an object containing neither a valid \`error\` and \`result\`. At least one of them should not be \`undefined\``;
        } else {
          for (const key of Object.keys(result)) {
            if (key !== "error" && key !== "data" && key !== "meta") {
              err = `The object returned by ${what} has the unknown property ${key}.`;
              break;
            }
          }
        }
        if (err) {
          console.error(`Error encountered handling the endpoint ${arg.endpointName}.
              ${err}
              It needs to return an object with either the shape \`{ data: <value> }\` or \`{ error: <value> }\` that may contain an optional \`meta\` property.
              Object returned was:`, result);
        }
      }
      if (result.error)
        throw new HandledError(result.error, result.meta);
      return fulfillWithValue(await transformResponse(result.data, result.meta, arg.originalArgs), {
        fulfilledTimeStamp: Date.now(),
        baseQueryMeta: result.meta,
        [SHOULD_AUTOBATCH]: true
      });
    } catch (error) {
      let catchedError = error;
      if (catchedError instanceof HandledError) {
        let transformErrorResponse = defaultTransformResponse;
        if (endpointDefinition.query && endpointDefinition.transformErrorResponse) {
          transformErrorResponse = endpointDefinition.transformErrorResponse;
        }
        try {
          return rejectWithValue(await transformErrorResponse(catchedError.value, catchedError.meta, arg.originalArgs), {
            baseQueryMeta: catchedError.meta,
            [SHOULD_AUTOBATCH]: true
          });
        } catch (e) {
          catchedError = e;
        }
      }
      if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
        console.error(`An unhandled error occurred processing a request for the endpoint "${arg.endpointName}".
In the case of an unhandled error, no tags will be "provided" or "invalidated".`, catchedError);
      } else {
        console.error(catchedError);
      }
      throw catchedError;
    }
  };
  function isForcedQuery(arg, state) {
    const requestState = state[reducerPath]?.queries?.[arg.queryCacheKey];
    const baseFetchOnMountOrArgChange = state[reducerPath]?.config.refetchOnMountOrArgChange;
    const fulfilledVal = requestState?.fulfilledTimeStamp;
    const refetchVal = arg.forceRefetch ?? (arg.subscribe && baseFetchOnMountOrArgChange);
    if (refetchVal) {
      return refetchVal === true || (Number(/* @__PURE__ */ new Date()) - Number(fulfilledVal)) / 1e3 >= refetchVal;
    }
    return false;
  }
  const queryThunk = createAsyncThunk(`${reducerPath}/executeQuery`, executeEndpoint, {
    getPendingMeta() {
      return {
        startedTimeStamp: Date.now(),
        [SHOULD_AUTOBATCH]: true
      };
    },
    condition(queryThunkArgs, {
      getState
    }) {
      const state = getState();
      const requestState = state[reducerPath]?.queries?.[queryThunkArgs.queryCacheKey];
      const fulfilledVal = requestState?.fulfilledTimeStamp;
      const currentArg = queryThunkArgs.originalArgs;
      const previousArg = requestState?.originalArgs;
      const endpointDefinition = endpointDefinitions[queryThunkArgs.endpointName];
      if (isUpsertQuery(queryThunkArgs)) {
        return true;
      }
      if (requestState?.status === "pending") {
        return false;
      }
      if (isForcedQuery(queryThunkArgs, state)) {
        return true;
      }
      if (isQueryDefinition$1(endpointDefinition) && endpointDefinition?.forceRefetch?.({
        currentArg,
        previousArg,
        endpointState: requestState,
        state
      })) {
        return true;
      }
      if (fulfilledVal) {
        return false;
      }
      return true;
    },
    dispatchConditionRejection: true
  });
  const mutationThunk = createAsyncThunk(`${reducerPath}/executeMutation`, executeEndpoint, {
    getPendingMeta() {
      return {
        startedTimeStamp: Date.now(),
        [SHOULD_AUTOBATCH]: true
      };
    }
  });
  const hasTheForce = (options) => "force" in options;
  const hasMaxAge = (options) => "ifOlderThan" in options;
  const prefetch = (endpointName, arg, options) => (dispatch, getState) => {
    const force = hasTheForce(options) && options.force;
    const maxAge = hasMaxAge(options) && options.ifOlderThan;
    const queryAction = (force2 = true) => {
      const options2 = {
        forceRefetch: force2,
        isPrefetch: true
      };
      return api.endpoints[endpointName].initiate(arg, options2);
    };
    const latestStateValue = api.endpoints[endpointName].select(arg)(getState());
    if (force) {
      dispatch(queryAction());
    } else if (maxAge) {
      const lastFulfilledTs = latestStateValue?.fulfilledTimeStamp;
      if (!lastFulfilledTs) {
        dispatch(queryAction());
        return;
      }
      const shouldRetrigger = (Number(/* @__PURE__ */ new Date()) - Number(new Date(lastFulfilledTs))) / 1e3 >= maxAge;
      if (shouldRetrigger) {
        dispatch(queryAction());
      }
    } else {
      dispatch(queryAction(false));
    }
  };
  function matchesEndpoint(endpointName) {
    return (action) => action?.meta?.arg?.endpointName === endpointName;
  }
  function buildMatchThunkActions(thunk, endpointName) {
    return {
      matchPending: isAllOf(isPending(thunk), matchesEndpoint(endpointName)),
      matchFulfilled: isAllOf(isFulfilled(thunk), matchesEndpoint(endpointName)),
      matchRejected: isAllOf(isRejected(thunk), matchesEndpoint(endpointName))
    };
  }
  return {
    queryThunk,
    mutationThunk,
    prefetch,
    updateQueryData,
    upsertQueryData,
    patchQueryData,
    buildMatchThunkActions
  };
}
function calculateProvidedByThunk(action, type, endpointDefinitions, assertTagType) {
  return calculateProvidedBy(endpointDefinitions[action.meta.arg.endpointName][type], isFulfilled(action) ? action.payload : void 0, isRejectedWithValue(action) ? action.payload : void 0, action.meta.arg.originalArgs, "baseQueryMeta" in action.meta ? action.meta.baseQueryMeta : void 0, assertTagType);
}
function updateQuerySubstateIfExists(state, queryCacheKey, update) {
  const substate = state[queryCacheKey];
  if (substate) {
    update(substate);
  }
}
function getMutationCacheKey(id) {
  return ("arg" in id ? id.arg.fixedCacheKey : id.fixedCacheKey) ?? id.requestId;
}
function updateMutationSubstateIfExists(state, id, update) {
  const substate = state[getMutationCacheKey(id)];
  if (substate) {
    update(substate);
  }
}
var initialState$1 = {};
function buildSlice({
  reducerPath,
  queryThunk,
  mutationThunk,
  context: {
    endpointDefinitions: definitions,
    apiUid,
    extractRehydrationInfo,
    hasRehydrationInfo
  },
  assertTagType,
  config
}) {
  const resetApiState = createAction(`${reducerPath}/resetApiState`);
  const querySlice = createSlice({
    name: `${reducerPath}/queries`,
    initialState: initialState$1,
    reducers: {
      removeQueryResult: {
        reducer(draft, {
          payload: {
            queryCacheKey
          }
        }) {
          delete draft[queryCacheKey];
        },
        prepare: prepareAutoBatched()
      },
      queryResultPatched: {
        reducer(draft, {
          payload: {
            queryCacheKey,
            patches
          }
        }) {
          updateQuerySubstateIfExists(draft, queryCacheKey, (substate) => {
            substate.data = applyPatches(substate.data, patches.concat());
          });
        },
        prepare: prepareAutoBatched()
      }
    },
    extraReducers(builder) {
      builder.addCase(queryThunk.pending, (draft, {
        meta,
        meta: {
          arg
        }
      }) => {
        const upserting = isUpsertQuery(arg);
        draft[arg.queryCacheKey] ??= {
          status: "uninitialized" /* uninitialized */,
          endpointName: arg.endpointName
        };
        updateQuerySubstateIfExists(draft, arg.queryCacheKey, (substate) => {
          substate.status = "pending" /* pending */;
          substate.requestId = upserting && substate.requestId ? (
            // for `upsertQuery` **updates**, keep the current `requestId`
            substate.requestId
          ) : (
            // for normal queries or `upsertQuery` **inserts** always update the `requestId`
            meta.requestId
          );
          if (arg.originalArgs !== void 0) {
            substate.originalArgs = arg.originalArgs;
          }
          substate.startedTimeStamp = meta.startedTimeStamp;
        });
      }).addCase(queryThunk.fulfilled, (draft, {
        meta,
        payload
      }) => {
        updateQuerySubstateIfExists(draft, meta.arg.queryCacheKey, (substate) => {
          if (substate.requestId !== meta.requestId && !isUpsertQuery(meta.arg))
            return;
          const {
            merge
          } = definitions[meta.arg.endpointName];
          substate.status = "fulfilled" /* fulfilled */;
          if (merge) {
            if (substate.data !== void 0) {
              const {
                fulfilledTimeStamp,
                arg,
                baseQueryMeta,
                requestId
              } = meta;
              let newData = createNextState(substate.data, (draftSubstateData) => {
                return merge(draftSubstateData, payload, {
                  arg: arg.originalArgs,
                  baseQueryMeta,
                  fulfilledTimeStamp,
                  requestId
                });
              });
              substate.data = newData;
            } else {
              substate.data = payload;
            }
          } else {
            substate.data = definitions[meta.arg.endpointName].structuralSharing ?? true ? copyWithStructuralSharing(isDraft(substate.data) ? original(substate.data) : substate.data, payload) : payload;
          }
          delete substate.error;
          substate.fulfilledTimeStamp = meta.fulfilledTimeStamp;
        });
      }).addCase(queryThunk.rejected, (draft, {
        meta: {
          condition,
          arg,
          requestId
        },
        error,
        payload
      }) => {
        updateQuerySubstateIfExists(draft, arg.queryCacheKey, (substate) => {
          if (condition) ; else {
            if (substate.requestId !== requestId)
              return;
            substate.status = "rejected" /* rejected */;
            substate.error = payload ?? error;
          }
        });
      }).addMatcher(hasRehydrationInfo, (draft, action) => {
        const {
          queries
        } = extractRehydrationInfo(action);
        for (const [key, entry] of Object.entries(queries)) {
          if (
            // do not rehydrate entries that were currently in flight.
            entry?.status === "fulfilled" /* fulfilled */ || entry?.status === "rejected" /* rejected */
          ) {
            draft[key] = entry;
          }
        }
      });
    }
  });
  const mutationSlice = createSlice({
    name: `${reducerPath}/mutations`,
    initialState: initialState$1,
    reducers: {
      removeMutationResult: {
        reducer(draft, {
          payload
        }) {
          const cacheKey = getMutationCacheKey(payload);
          if (cacheKey in draft) {
            delete draft[cacheKey];
          }
        },
        prepare: prepareAutoBatched()
      }
    },
    extraReducers(builder) {
      builder.addCase(mutationThunk.pending, (draft, {
        meta,
        meta: {
          requestId,
          arg,
          startedTimeStamp
        }
      }) => {
        if (!arg.track)
          return;
        draft[getMutationCacheKey(meta)] = {
          requestId,
          status: "pending" /* pending */,
          endpointName: arg.endpointName,
          startedTimeStamp
        };
      }).addCase(mutationThunk.fulfilled, (draft, {
        payload,
        meta
      }) => {
        if (!meta.arg.track)
          return;
        updateMutationSubstateIfExists(draft, meta, (substate) => {
          if (substate.requestId !== meta.requestId)
            return;
          substate.status = "fulfilled" /* fulfilled */;
          substate.data = payload;
          substate.fulfilledTimeStamp = meta.fulfilledTimeStamp;
        });
      }).addCase(mutationThunk.rejected, (draft, {
        payload,
        error,
        meta
      }) => {
        if (!meta.arg.track)
          return;
        updateMutationSubstateIfExists(draft, meta, (substate) => {
          if (substate.requestId !== meta.requestId)
            return;
          substate.status = "rejected" /* rejected */;
          substate.error = payload ?? error;
        });
      }).addMatcher(hasRehydrationInfo, (draft, action) => {
        const {
          mutations
        } = extractRehydrationInfo(action);
        for (const [key, entry] of Object.entries(mutations)) {
          if (
            // do not rehydrate entries that were currently in flight.
            (entry?.status === "fulfilled" /* fulfilled */ || entry?.status === "rejected" /* rejected */) && // only rehydrate endpoints that were persisted using a `fixedCacheKey`
            key !== entry?.requestId
          ) {
            draft[key] = entry;
          }
        }
      });
    }
  });
  const invalidationSlice = createSlice({
    name: `${reducerPath}/invalidation`,
    initialState: initialState$1,
    reducers: {
      updateProvidedBy: {
        reducer(draft, action) {
          const {
            queryCacheKey,
            providedTags
          } = action.payload;
          for (const tagTypeSubscriptions of Object.values(draft)) {
            for (const idSubscriptions of Object.values(tagTypeSubscriptions)) {
              const foundAt = idSubscriptions.indexOf(queryCacheKey);
              if (foundAt !== -1) {
                idSubscriptions.splice(foundAt, 1);
              }
            }
          }
          for (const {
            type,
            id
          } of providedTags) {
            const subscribedQueries = (draft[type] ??= {})[id || "__internal_without_id"] ??= [];
            const alreadySubscribed = subscribedQueries.includes(queryCacheKey);
            if (!alreadySubscribed) {
              subscribedQueries.push(queryCacheKey);
            }
          }
        },
        prepare: prepareAutoBatched()
      }
    },
    extraReducers(builder) {
      builder.addCase(querySlice.actions.removeQueryResult, (draft, {
        payload: {
          queryCacheKey
        }
      }) => {
        for (const tagTypeSubscriptions of Object.values(draft)) {
          for (const idSubscriptions of Object.values(tagTypeSubscriptions)) {
            const foundAt = idSubscriptions.indexOf(queryCacheKey);
            if (foundAt !== -1) {
              idSubscriptions.splice(foundAt, 1);
            }
          }
        }
      }).addMatcher(hasRehydrationInfo, (draft, action) => {
        const {
          provided
        } = extractRehydrationInfo(action);
        for (const [type, incomingTags] of Object.entries(provided)) {
          for (const [id, cacheKeys] of Object.entries(incomingTags)) {
            const subscribedQueries = (draft[type] ??= {})[id || "__internal_without_id"] ??= [];
            for (const queryCacheKey of cacheKeys) {
              const alreadySubscribed = subscribedQueries.includes(queryCacheKey);
              if (!alreadySubscribed) {
                subscribedQueries.push(queryCacheKey);
              }
            }
          }
        }
      }).addMatcher(isAnyOf(isFulfilled(queryThunk), isRejectedWithValue(queryThunk)), (draft, action) => {
        const providedTags = calculateProvidedByThunk(action, "providesTags", definitions, assertTagType);
        const {
          queryCacheKey
        } = action.meta.arg;
        invalidationSlice.caseReducers.updateProvidedBy(draft, invalidationSlice.actions.updateProvidedBy({
          queryCacheKey,
          providedTags
        }));
      });
    }
  });
  const subscriptionSlice = createSlice({
    name: `${reducerPath}/subscriptions`,
    initialState: initialState$1,
    reducers: {
      updateSubscriptionOptions(d, a) {
      },
      unsubscribeQueryResult(d, a) {
      },
      internal_getRTKQSubscriptions() {
      }
    }
  });
  const internalSubscriptionsSlice = createSlice({
    name: `${reducerPath}/internalSubscriptions`,
    initialState: initialState$1,
    reducers: {
      subscriptionsUpdated: {
        reducer(state, action) {
          return applyPatches(state, action.payload);
        },
        prepare: prepareAutoBatched()
      }
    }
  });
  const configSlice = createSlice({
    name: `${reducerPath}/config`,
    initialState: {
      online: isOnline(),
      focused: isDocumentVisible(),
      middlewareRegistered: false,
      ...config
    },
    reducers: {
      middlewareRegistered(state, {
        payload
      }) {
        state.middlewareRegistered = state.middlewareRegistered === "conflict" || apiUid !== payload ? "conflict" : true;
      }
    },
    extraReducers: (builder) => {
      builder.addCase(onOnline, (state) => {
        state.online = true;
      }).addCase(onOffline, (state) => {
        state.online = false;
      }).addCase(onFocus, (state) => {
        state.focused = true;
      }).addCase(onFocusLost, (state) => {
        state.focused = false;
      }).addMatcher(hasRehydrationInfo, (draft) => ({
        ...draft
      }));
    }
  });
  const combinedReducer = combineReducers({
    queries: querySlice.reducer,
    mutations: mutationSlice.reducer,
    provided: invalidationSlice.reducer,
    subscriptions: internalSubscriptionsSlice.reducer,
    config: configSlice.reducer
  });
  const reducer = (state, action) => combinedReducer(resetApiState.match(action) ? void 0 : state, action);
  const actions = {
    ...configSlice.actions,
    ...querySlice.actions,
    ...subscriptionSlice.actions,
    ...internalSubscriptionsSlice.actions,
    ...mutationSlice.actions,
    ...invalidationSlice.actions,
    resetApiState
  };
  return {
    reducer,
    actions
  };
}

// src/query/core/buildSelectors.ts
var skipToken = /* @__PURE__ */ Symbol.for("RTKQ/skipToken");
var initialSubState = {
  status: "uninitialized" /* uninitialized */
};
var defaultQuerySubState = /* @__PURE__ */ createNextState(initialSubState, () => {
});
var defaultMutationSubState = /* @__PURE__ */ createNextState(initialSubState, () => {
});
function buildSelectors({
  serializeQueryArgs,
  reducerPath,
  createSelector: createSelector2
}) {
  const selectSkippedQuery = (state) => defaultQuerySubState;
  const selectSkippedMutation = (state) => defaultMutationSubState;
  return {
    buildQuerySelector,
    buildMutationSelector,
    selectInvalidatedBy,
    selectCachedArgsForQuery
  };
  function withRequestFlags(substate) {
    return {
      ...substate,
      ...getRequestStatusFlags(substate.status)
    };
  }
  function selectInternalState(rootState) {
    const state = rootState[reducerPath];
    if (process.env.NODE_ENV !== "production") {
      if (!state) {
        if (selectInternalState.triggered)
          return state;
        selectInternalState.triggered = true;
        console.error(`Error: No data found at \`state.${reducerPath}\`. Did you forget to add the reducer to the store?`);
      }
    }
    return state;
  }
  function buildQuerySelector(endpointName, endpointDefinition) {
    return (queryArgs) => {
      const serializedArgs = serializeQueryArgs({
        queryArgs,
        endpointDefinition,
        endpointName
      });
      const selectQuerySubstate = (state) => selectInternalState(state)?.queries?.[serializedArgs] ?? defaultQuerySubState;
      const finalSelectQuerySubState = queryArgs === skipToken ? selectSkippedQuery : selectQuerySubstate;
      return createSelector2(finalSelectQuerySubState, withRequestFlags);
    };
  }
  function buildMutationSelector() {
    return (id) => {
      let mutationId;
      if (typeof id === "object") {
        mutationId = getMutationCacheKey(id) ?? skipToken;
      } else {
        mutationId = id;
      }
      const selectMutationSubstate = (state) => selectInternalState(state)?.mutations?.[mutationId] ?? defaultMutationSubState;
      const finalSelectMutationSubstate = mutationId === skipToken ? selectSkippedMutation : selectMutationSubstate;
      return createSelector2(finalSelectMutationSubstate, withRequestFlags);
    };
  }
  function selectInvalidatedBy(state, tags) {
    const apiState = state[reducerPath];
    const toInvalidate = /* @__PURE__ */ new Set();
    for (const tag of tags.map(expandTagDescription)) {
      const provided = apiState.provided[tag.type];
      if (!provided) {
        continue;
      }
      let invalidateSubscriptions = (tag.id !== void 0 ? (
        // id given: invalidate all queries that provide this type & id
        provided[tag.id]
      ) : (
        // no id: invalidate all queries that provide this type
        flatten(Object.values(provided))
      )) ?? [];
      for (const invalidate of invalidateSubscriptions) {
        toInvalidate.add(invalidate);
      }
    }
    return flatten(Array.from(toInvalidate.values()).map((queryCacheKey) => {
      const querySubState = apiState.queries[queryCacheKey];
      return querySubState ? [{
        queryCacheKey,
        endpointName: querySubState.endpointName,
        originalArgs: querySubState.originalArgs
      }] : [];
    }));
  }
  function selectCachedArgsForQuery(state, queryName) {
    return Object.values(state[reducerPath].queries).filter((entry) => entry?.endpointName === queryName && entry.status !== "uninitialized" /* uninitialized */).map((entry) => entry.originalArgs);
  }
}

// src/query/defaultSerializeQueryArgs.ts
var cache$1 = WeakMap ? /* @__PURE__ */ new WeakMap() : void 0;
var defaultSerializeQueryArgs$1 = ({
  endpointName,
  queryArgs
}) => {
  let serialized = "";
  const cached = cache$1?.get(queryArgs);
  if (typeof cached === "string") {
    serialized = cached;
  } else {
    const stringified = JSON.stringify(queryArgs, (key, value) => {
      value = typeof value === "bigint" ? {
        $bigint: value.toString()
      } : value;
      value = isPlainObject$1(value) ? Object.keys(value).sort().reduce((acc, key2) => {
        acc[key2] = value[key2];
        return acc;
      }, {}) : value;
      return value;
    });
    if (isPlainObject$1(queryArgs)) {
      cache$1?.set(queryArgs, stringified);
    }
    serialized = stringified;
  }
  return `${endpointName}(${serialized})`;
};
function buildCreateApi(...modules) {
  return function baseCreateApi(options) {
    const extractRehydrationInfo = weakMapMemoize((action) => options.extractRehydrationInfo?.(action, {
      reducerPath: options.reducerPath ?? "api"
    }));
    const optionsWithDefaults = {
      reducerPath: "api",
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      invalidationBehavior: "delayed",
      ...options,
      extractRehydrationInfo,
      serializeQueryArgs(queryArgsApi) {
        let finalSerializeQueryArgs = defaultSerializeQueryArgs$1;
        if ("serializeQueryArgs" in queryArgsApi.endpointDefinition) {
          const endpointSQA = queryArgsApi.endpointDefinition.serializeQueryArgs;
          finalSerializeQueryArgs = (queryArgsApi2) => {
            const initialResult = endpointSQA(queryArgsApi2);
            if (typeof initialResult === "string") {
              return initialResult;
            } else {
              return defaultSerializeQueryArgs$1({
                ...queryArgsApi2,
                queryArgs: initialResult
              });
            }
          };
        } else if (options.serializeQueryArgs) {
          finalSerializeQueryArgs = options.serializeQueryArgs;
        }
        return finalSerializeQueryArgs(queryArgsApi);
      },
      tagTypes: [...options.tagTypes || []]
    };
    const context = {
      endpointDefinitions: {},
      batch(fn) {
        fn();
      },
      apiUid: nanoid(),
      extractRehydrationInfo,
      hasRehydrationInfo: weakMapMemoize((action) => extractRehydrationInfo(action) != null)
    };
    const api = {
      injectEndpoints,
      enhanceEndpoints({
        addTagTypes,
        endpoints
      }) {
        if (addTagTypes) {
          for (const eT of addTagTypes) {
            if (!optionsWithDefaults.tagTypes.includes(eT)) {
              optionsWithDefaults.tagTypes.push(eT);
            }
          }
        }
        if (endpoints) {
          for (const [endpointName, partialDefinition] of Object.entries(endpoints)) {
            if (typeof partialDefinition === "function") {
              partialDefinition(context.endpointDefinitions[endpointName]);
            } else {
              Object.assign(context.endpointDefinitions[endpointName] || {}, partialDefinition);
            }
          }
        }
        return api;
      }
    };
    const initializedModules = modules.map((m) => m.init(api, optionsWithDefaults, context));
    function injectEndpoints(inject) {
      const evaluatedEndpoints = inject.endpoints({
        query: (x) => ({
          ...x,
          type: "query" /* query */
        }),
        mutation: (x) => ({
          ...x,
          type: "mutation" /* mutation */
        })
      });
      for (const [endpointName, definition] of Object.entries(evaluatedEndpoints)) {
        if (inject.overrideExisting !== true && endpointName in context.endpointDefinitions) {
          if (inject.overrideExisting === "throw") {
            throw new Error(process.env.NODE_ENV === "production" ? formatProdErrorMessage(39) : `called \`injectEndpoints\` to override already-existing endpointName ${endpointName} without specifying \`overrideExisting: true\``);
          } else if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
            console.error(`called \`injectEndpoints\` to override already-existing endpointName ${endpointName} without specifying \`overrideExisting: true\``);
          }
          continue;
        }
        context.endpointDefinitions[endpointName] = definition;
        for (const m of initializedModules) {
          m.injectEndpoint(endpointName, definition);
        }
      }
      return api;
    }
    return api.injectEndpoints({
      endpoints: options.endpoints
    });
  };
}
function isObjectEmpty(obj) {
  for (let k in obj) {
    return false;
  }
  return true;
}
var THIRTY_TWO_BIT_MAX_TIMER_SECONDS = 2147483647 / 1e3 - 1;
var buildCacheCollectionHandler = ({
  reducerPath,
  api,
  queryThunk,
  context,
  internalState
}) => {
  const {
    removeQueryResult,
    unsubscribeQueryResult
  } = api.internalActions;
  const canTriggerUnsubscribe = isAnyOf(unsubscribeQueryResult.match, queryThunk.fulfilled, queryThunk.rejected);
  function anySubscriptionsRemainingForKey(queryCacheKey) {
    const subscriptions = internalState.currentSubscriptions[queryCacheKey];
    return !!subscriptions && !isObjectEmpty(subscriptions);
  }
  const currentRemovalTimeouts = {};
  const handler = (action, mwApi, internalState2) => {
    if (canTriggerUnsubscribe(action)) {
      const state = mwApi.getState()[reducerPath];
      const {
        queryCacheKey
      } = unsubscribeQueryResult.match(action) ? action.payload : action.meta.arg;
      handleUnsubscribe(queryCacheKey, state.queries[queryCacheKey]?.endpointName, mwApi, state.config);
    }
    if (api.util.resetApiState.match(action)) {
      for (const [key, timeout] of Object.entries(currentRemovalTimeouts)) {
        if (timeout)
          clearTimeout(timeout);
        delete currentRemovalTimeouts[key];
      }
    }
    if (context.hasRehydrationInfo(action)) {
      const state = mwApi.getState()[reducerPath];
      const {
        queries
      } = context.extractRehydrationInfo(action);
      for (const [queryCacheKey, queryState] of Object.entries(queries)) {
        handleUnsubscribe(queryCacheKey, queryState?.endpointName, mwApi, state.config);
      }
    }
  };
  function handleUnsubscribe(queryCacheKey, endpointName, api2, config) {
    const endpointDefinition = context.endpointDefinitions[endpointName];
    const keepUnusedDataFor = endpointDefinition?.keepUnusedDataFor ?? config.keepUnusedDataFor;
    if (keepUnusedDataFor === Infinity) {
      return;
    }
    const finalKeepUnusedDataFor = Math.max(0, Math.min(keepUnusedDataFor, THIRTY_TWO_BIT_MAX_TIMER_SECONDS));
    if (!anySubscriptionsRemainingForKey(queryCacheKey)) {
      const currentTimeout = currentRemovalTimeouts[queryCacheKey];
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
      currentRemovalTimeouts[queryCacheKey] = setTimeout(() => {
        if (!anySubscriptionsRemainingForKey(queryCacheKey)) {
          api2.dispatch(removeQueryResult({
            queryCacheKey
          }));
        }
        delete currentRemovalTimeouts[queryCacheKey];
      }, finalKeepUnusedDataFor * 1e3);
    }
  }
  return handler;
};

// src/query/core/buildMiddleware/invalidationByTags.ts
var buildInvalidationByTagsHandler = ({
  reducerPath,
  context,
  context: {
    endpointDefinitions
  },
  mutationThunk,
  queryThunk,
  api,
  assertTagType,
  refetchQuery,
  internalState
}) => {
  const {
    removeQueryResult
  } = api.internalActions;
  const isThunkActionWithTags = isAnyOf(isFulfilled(mutationThunk), isRejectedWithValue(mutationThunk));
  const isQueryEnd = isAnyOf(isFulfilled(mutationThunk, queryThunk), isRejected(mutationThunk, queryThunk));
  let pendingTagInvalidations = [];
  const handler = (action, mwApi) => {
    if (isThunkActionWithTags(action)) {
      invalidateTags(calculateProvidedByThunk(action, "invalidatesTags", endpointDefinitions, assertTagType), mwApi);
    } else if (isQueryEnd(action)) {
      invalidateTags([], mwApi);
    } else if (api.util.invalidateTags.match(action)) {
      invalidateTags(calculateProvidedBy(action.payload, void 0, void 0, void 0, void 0, assertTagType), mwApi);
    }
  };
  function hasPendingRequests(state) {
    for (const key in state.queries) {
      if (state.queries[key]?.status === "pending" /* pending */)
        return true;
    }
    for (const key in state.mutations) {
      if (state.mutations[key]?.status === "pending" /* pending */)
        return true;
    }
    return false;
  }
  function invalidateTags(newTags, mwApi) {
    const rootState = mwApi.getState();
    const state = rootState[reducerPath];
    pendingTagInvalidations.push(...newTags);
    if (state.config.invalidationBehavior === "delayed" && hasPendingRequests(state)) {
      return;
    }
    const tags = pendingTagInvalidations;
    pendingTagInvalidations = [];
    if (tags.length === 0)
      return;
    const toInvalidate = api.util.selectInvalidatedBy(rootState, tags);
    context.batch(() => {
      const valuesArray = Array.from(toInvalidate.values());
      for (const {
        queryCacheKey
      } of valuesArray) {
        const querySubState = state.queries[queryCacheKey];
        const subscriptionSubState = internalState.currentSubscriptions[queryCacheKey] ?? {};
        if (querySubState) {
          if (countObjectKeys$1(subscriptionSubState) === 0) {
            mwApi.dispatch(removeQueryResult({
              queryCacheKey
            }));
          } else if (querySubState.status !== "uninitialized" /* uninitialized */) {
            mwApi.dispatch(refetchQuery(querySubState, queryCacheKey));
          }
        }
      }
    });
  }
  return handler;
};

// src/query/core/buildMiddleware/polling.ts
var buildPollingHandler = ({
  reducerPath,
  queryThunk,
  api,
  refetchQuery,
  internalState
}) => {
  const currentPolls = {};
  const handler = (action, mwApi) => {
    if (api.internalActions.updateSubscriptionOptions.match(action) || api.internalActions.unsubscribeQueryResult.match(action)) {
      updatePollingInterval(action.payload, mwApi);
    }
    if (queryThunk.pending.match(action) || queryThunk.rejected.match(action) && action.meta.condition) {
      updatePollingInterval(action.meta.arg, mwApi);
    }
    if (queryThunk.fulfilled.match(action) || queryThunk.rejected.match(action) && !action.meta.condition) {
      startNextPoll(action.meta.arg, mwApi);
    }
    if (api.util.resetApiState.match(action)) {
      clearPolls();
    }
  };
  function startNextPoll({
    queryCacheKey
  }, api2) {
    const state = api2.getState()[reducerPath];
    const querySubState = state.queries[queryCacheKey];
    const subscriptions = internalState.currentSubscriptions[queryCacheKey];
    if (!querySubState || querySubState.status === "uninitialized" /* uninitialized */)
      return;
    const {
      lowestPollingInterval,
      skipPollingIfUnfocused
    } = findLowestPollingInterval(subscriptions);
    if (!Number.isFinite(lowestPollingInterval))
      return;
    const currentPoll = currentPolls[queryCacheKey];
    if (currentPoll?.timeout) {
      clearTimeout(currentPoll.timeout);
      currentPoll.timeout = void 0;
    }
    const nextPollTimestamp = Date.now() + lowestPollingInterval;
    currentPolls[queryCacheKey] = {
      nextPollTimestamp,
      pollingInterval: lowestPollingInterval,
      timeout: setTimeout(() => {
        if (state.config.focused || !skipPollingIfUnfocused) {
          api2.dispatch(refetchQuery(querySubState, queryCacheKey));
        }
        startNextPoll({
          queryCacheKey
        }, api2);
      }, lowestPollingInterval)
    };
  }
  function updatePollingInterval({
    queryCacheKey
  }, api2) {
    const state = api2.getState()[reducerPath];
    const querySubState = state.queries[queryCacheKey];
    const subscriptions = internalState.currentSubscriptions[queryCacheKey];
    if (!querySubState || querySubState.status === "uninitialized" /* uninitialized */) {
      return;
    }
    const {
      lowestPollingInterval
    } = findLowestPollingInterval(subscriptions);
    if (!Number.isFinite(lowestPollingInterval)) {
      cleanupPollForKey(queryCacheKey);
      return;
    }
    const currentPoll = currentPolls[queryCacheKey];
    const nextPollTimestamp = Date.now() + lowestPollingInterval;
    if (!currentPoll || nextPollTimestamp < currentPoll.nextPollTimestamp) {
      startNextPoll({
        queryCacheKey
      }, api2);
    }
  }
  function cleanupPollForKey(key) {
    const existingPoll = currentPolls[key];
    if (existingPoll?.timeout) {
      clearTimeout(existingPoll.timeout);
    }
    delete currentPolls[key];
  }
  function clearPolls() {
    for (const key of Object.keys(currentPolls)) {
      cleanupPollForKey(key);
    }
  }
  function findLowestPollingInterval(subscribers = {}) {
    let skipPollingIfUnfocused = false;
    let lowestPollingInterval = Number.POSITIVE_INFINITY;
    for (let key in subscribers) {
      if (!!subscribers[key].pollingInterval) {
        lowestPollingInterval = Math.min(subscribers[key].pollingInterval, lowestPollingInterval);
        skipPollingIfUnfocused = subscribers[key].skipPollingIfUnfocused || skipPollingIfUnfocused;
      }
    }
    return {
      lowestPollingInterval,
      skipPollingIfUnfocused
    };
  }
  return handler;
};

// src/query/core/buildMiddleware/windowEventHandling.ts
var buildWindowEventHandler = ({
  reducerPath,
  context,
  api,
  refetchQuery,
  internalState
}) => {
  const {
    removeQueryResult
  } = api.internalActions;
  const handler = (action, mwApi) => {
    if (onFocus.match(action)) {
      refetchValidQueries(mwApi, "refetchOnFocus");
    }
    if (onOnline.match(action)) {
      refetchValidQueries(mwApi, "refetchOnReconnect");
    }
  };
  function refetchValidQueries(api2, type) {
    const state = api2.getState()[reducerPath];
    const queries = state.queries;
    const subscriptions = internalState.currentSubscriptions;
    context.batch(() => {
      for (const queryCacheKey of Object.keys(subscriptions)) {
        const querySubState = queries[queryCacheKey];
        const subscriptionSubState = subscriptions[queryCacheKey];
        if (!subscriptionSubState || !querySubState)
          continue;
        const shouldRefetch = Object.values(subscriptionSubState).some((sub) => sub[type] === true) || Object.values(subscriptionSubState).every((sub) => sub[type] === void 0) && state.config[type];
        if (shouldRefetch) {
          if (countObjectKeys$1(subscriptionSubState) === 0) {
            api2.dispatch(removeQueryResult({
              queryCacheKey
            }));
          } else if (querySubState.status !== "uninitialized" /* uninitialized */) {
            api2.dispatch(refetchQuery(querySubState, queryCacheKey));
          }
        }
      }
    });
  }
  return handler;
};

// src/query/core/buildMiddleware/cacheLifecycle.ts
var neverResolvedError = new Error("Promise never resolved before cacheEntryRemoved.");
var buildCacheLifecycleHandler = ({
  api,
  reducerPath,
  context,
  queryThunk,
  mutationThunk,
  internalState
}) => {
  const isQueryThunk = isAsyncThunkAction(queryThunk);
  const isMutationThunk = isAsyncThunkAction(mutationThunk);
  const isFulfilledThunk = isFulfilled(queryThunk, mutationThunk);
  const lifecycleMap = {};
  const handler = (action, mwApi, stateBefore) => {
    const cacheKey = getCacheKey(action);
    if (queryThunk.pending.match(action)) {
      const oldState = stateBefore[reducerPath].queries[cacheKey];
      const state = mwApi.getState()[reducerPath].queries[cacheKey];
      if (!oldState && state) {
        handleNewKey(action.meta.arg.endpointName, action.meta.arg.originalArgs, cacheKey, mwApi, action.meta.requestId);
      }
    } else if (mutationThunk.pending.match(action)) {
      const state = mwApi.getState()[reducerPath].mutations[cacheKey];
      if (state) {
        handleNewKey(action.meta.arg.endpointName, action.meta.arg.originalArgs, cacheKey, mwApi, action.meta.requestId);
      }
    } else if (isFulfilledThunk(action)) {
      const lifecycle = lifecycleMap[cacheKey];
      if (lifecycle?.valueResolved) {
        lifecycle.valueResolved({
          data: action.payload,
          meta: action.meta.baseQueryMeta
        });
        delete lifecycle.valueResolved;
      }
    } else if (api.internalActions.removeQueryResult.match(action) || api.internalActions.removeMutationResult.match(action)) {
      const lifecycle = lifecycleMap[cacheKey];
      if (lifecycle) {
        delete lifecycleMap[cacheKey];
        lifecycle.cacheEntryRemoved();
      }
    } else if (api.util.resetApiState.match(action)) {
      for (const [cacheKey2, lifecycle] of Object.entries(lifecycleMap)) {
        delete lifecycleMap[cacheKey2];
        lifecycle.cacheEntryRemoved();
      }
    }
  };
  function getCacheKey(action) {
    if (isQueryThunk(action))
      return action.meta.arg.queryCacheKey;
    if (isMutationThunk(action)) {
      return action.meta.arg.fixedCacheKey ?? action.meta.requestId;
    }
    if (api.internalActions.removeQueryResult.match(action))
      return action.payload.queryCacheKey;
    if (api.internalActions.removeMutationResult.match(action))
      return getMutationCacheKey(action.payload);
    return "";
  }
  function handleNewKey(endpointName, originalArgs, queryCacheKey, mwApi, requestId) {
    const endpointDefinition = context.endpointDefinitions[endpointName];
    const onCacheEntryAdded = endpointDefinition?.onCacheEntryAdded;
    if (!onCacheEntryAdded)
      return;
    let lifecycle = {};
    const cacheEntryRemoved = new Promise((resolve) => {
      lifecycle.cacheEntryRemoved = resolve;
    });
    const cacheDataLoaded = Promise.race([new Promise((resolve) => {
      lifecycle.valueResolved = resolve;
    }), cacheEntryRemoved.then(() => {
      throw neverResolvedError;
    })]);
    cacheDataLoaded.catch(() => {
    });
    lifecycleMap[queryCacheKey] = lifecycle;
    const selector = api.endpoints[endpointName].select(endpointDefinition.type === "query" /* query */ ? originalArgs : queryCacheKey);
    const extra = mwApi.dispatch((_, __, extra2) => extra2);
    const lifecycleApi = {
      ...mwApi,
      getCacheEntry: () => selector(mwApi.getState()),
      requestId,
      extra,
      updateCachedData: endpointDefinition.type === "query" /* query */ ? (updateRecipe) => mwApi.dispatch(api.util.updateQueryData(endpointName, originalArgs, updateRecipe)) : void 0,
      cacheDataLoaded,
      cacheEntryRemoved
    };
    const runningHandler = onCacheEntryAdded(originalArgs, lifecycleApi);
    Promise.resolve(runningHandler).catch((e) => {
      if (e === neverResolvedError)
        return;
      throw e;
    });
  }
  return handler;
};

// src/query/core/buildMiddleware/queryLifecycle.ts
var buildQueryLifecycleHandler = ({
  api,
  context,
  queryThunk,
  mutationThunk
}) => {
  const isPendingThunk = isPending(queryThunk, mutationThunk);
  const isRejectedThunk = isRejected(queryThunk, mutationThunk);
  const isFullfilledThunk = isFulfilled(queryThunk, mutationThunk);
  const lifecycleMap = {};
  const handler = (action, mwApi) => {
    if (isPendingThunk(action)) {
      const {
        requestId,
        arg: {
          endpointName,
          originalArgs
        }
      } = action.meta;
      const endpointDefinition = context.endpointDefinitions[endpointName];
      const onQueryStarted = endpointDefinition?.onQueryStarted;
      if (onQueryStarted) {
        const lifecycle = {};
        const queryFulfilled = new Promise((resolve, reject) => {
          lifecycle.resolve = resolve;
          lifecycle.reject = reject;
        });
        queryFulfilled.catch(() => {
        });
        lifecycleMap[requestId] = lifecycle;
        const selector = api.endpoints[endpointName].select(endpointDefinition.type === "query" /* query */ ? originalArgs : requestId);
        const extra = mwApi.dispatch((_, __, extra2) => extra2);
        const lifecycleApi = {
          ...mwApi,
          getCacheEntry: () => selector(mwApi.getState()),
          requestId,
          extra,
          updateCachedData: endpointDefinition.type === "query" /* query */ ? (updateRecipe) => mwApi.dispatch(api.util.updateQueryData(endpointName, originalArgs, updateRecipe)) : void 0,
          queryFulfilled
        };
        onQueryStarted(originalArgs, lifecycleApi);
      }
    } else if (isFullfilledThunk(action)) {
      const {
        requestId,
        baseQueryMeta
      } = action.meta;
      lifecycleMap[requestId]?.resolve({
        data: action.payload,
        meta: baseQueryMeta
      });
      delete lifecycleMap[requestId];
    } else if (isRejectedThunk(action)) {
      const {
        requestId,
        rejectedWithValue,
        baseQueryMeta
      } = action.meta;
      lifecycleMap[requestId]?.reject({
        error: action.payload ?? action.error,
        isUnhandledError: !rejectedWithValue,
        meta: baseQueryMeta
      });
      delete lifecycleMap[requestId];
    }
  };
  return handler;
};

// src/query/core/buildMiddleware/devMiddleware.ts
var buildDevCheckHandler = ({
  api,
  context: {
    apiUid
  },
  reducerPath
}) => {
  return (action, mwApi) => {
    if (api.util.resetApiState.match(action)) {
      mwApi.dispatch(api.internalActions.middlewareRegistered(apiUid));
    }
    if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
      if (api.internalActions.middlewareRegistered.match(action) && action.payload === apiUid && mwApi.getState()[reducerPath]?.config?.middlewareRegistered === "conflict") {
        console.warn(`There is a mismatch between slice and middleware for the reducerPath "${reducerPath}".
You can only have one api per reducer path, this will lead to crashes in various situations!${reducerPath === "api" ? `
If you have multiple apis, you *have* to specify the reducerPath option when using createApi!` : ""}`);
      }
    }
  };
};
var buildBatchedActionsHandler = ({
  api,
  queryThunk,
  internalState
}) => {
  const subscriptionsPrefix = `${api.reducerPath}/subscriptions`;
  let previousSubscriptions = null;
  let updateSyncTimer = null;
  const {
    updateSubscriptionOptions,
    unsubscribeQueryResult
  } = api.internalActions;
  const actuallyMutateSubscriptions = (mutableState, action) => {
    if (updateSubscriptionOptions.match(action)) {
      const {
        queryCacheKey,
        requestId,
        options
      } = action.payload;
      if (mutableState?.[queryCacheKey]?.[requestId]) {
        mutableState[queryCacheKey][requestId] = options;
      }
      return true;
    }
    if (unsubscribeQueryResult.match(action)) {
      const {
        queryCacheKey,
        requestId
      } = action.payload;
      if (mutableState[queryCacheKey]) {
        delete mutableState[queryCacheKey][requestId];
      }
      return true;
    }
    if (api.internalActions.removeQueryResult.match(action)) {
      delete mutableState[action.payload.queryCacheKey];
      return true;
    }
    if (queryThunk.pending.match(action)) {
      const {
        meta: {
          arg,
          requestId
        }
      } = action;
      const substate = mutableState[arg.queryCacheKey] ??= {};
      substate[`${requestId}_running`] = {};
      if (arg.subscribe) {
        substate[requestId] = arg.subscriptionOptions ?? substate[requestId] ?? {};
      }
      return true;
    }
    let mutated = false;
    if (queryThunk.fulfilled.match(action) || queryThunk.rejected.match(action)) {
      const state = mutableState[action.meta.arg.queryCacheKey] || {};
      const key = `${action.meta.requestId}_running`;
      mutated ||= !!state[key];
      delete state[key];
    }
    if (queryThunk.rejected.match(action)) {
      const {
        meta: {
          condition,
          arg,
          requestId
        }
      } = action;
      if (condition && arg.subscribe) {
        const substate = mutableState[arg.queryCacheKey] ??= {};
        substate[requestId] = arg.subscriptionOptions ?? substate[requestId] ?? {};
        mutated = true;
      }
    }
    return mutated;
  };
  const getSubscriptions = () => internalState.currentSubscriptions;
  const getSubscriptionCount = (queryCacheKey) => {
    const subscriptions = getSubscriptions();
    const subscriptionsForQueryArg = subscriptions[queryCacheKey] ?? {};
    return countObjectKeys$1(subscriptionsForQueryArg);
  };
  const isRequestSubscribed = (queryCacheKey, requestId) => {
    const subscriptions = getSubscriptions();
    return !!subscriptions?.[queryCacheKey]?.[requestId];
  };
  const subscriptionSelectors = {
    getSubscriptions,
    getSubscriptionCount,
    isRequestSubscribed
  };
  return (action, mwApi) => {
    if (!previousSubscriptions) {
      previousSubscriptions = JSON.parse(JSON.stringify(internalState.currentSubscriptions));
    }
    if (api.util.resetApiState.match(action)) {
      previousSubscriptions = internalState.currentSubscriptions = {};
      updateSyncTimer = null;
      return [true, false];
    }
    if (api.internalActions.internal_getRTKQSubscriptions.match(action)) {
      return [false, subscriptionSelectors];
    }
    const didMutate = actuallyMutateSubscriptions(internalState.currentSubscriptions, action);
    let actionShouldContinue = true;
    if (didMutate) {
      if (!updateSyncTimer) {
        updateSyncTimer = setTimeout(() => {
          const newSubscriptions = JSON.parse(JSON.stringify(internalState.currentSubscriptions));
          const [, patches] = produceWithPatches(previousSubscriptions, () => newSubscriptions);
          mwApi.next(api.internalActions.subscriptionsUpdated(patches));
          previousSubscriptions = newSubscriptions;
          updateSyncTimer = null;
        }, 500);
      }
      const isSubscriptionSliceAction = typeof action.type == "string" && !!action.type.startsWith(subscriptionsPrefix);
      const isAdditionalSubscriptionAction = queryThunk.rejected.match(action) && action.meta.condition && !!action.meta.arg.subscribe;
      actionShouldContinue = !isSubscriptionSliceAction && !isAdditionalSubscriptionAction;
    }
    return [actionShouldContinue, false];
  };
};

// src/query/core/buildMiddleware/index.ts
function buildMiddleware(input) {
  const {
    reducerPath,
    queryThunk,
    api,
    context
  } = input;
  const {
    apiUid
  } = context;
  const actions = {
    invalidateTags: createAction(`${reducerPath}/invalidateTags`)
  };
  const isThisApiSliceAction = (action) => action.type.startsWith(`${reducerPath}/`);
  const handlerBuilders = [buildDevCheckHandler, buildCacheCollectionHandler, buildInvalidationByTagsHandler, buildPollingHandler, buildCacheLifecycleHandler, buildQueryLifecycleHandler];
  const middleware = (mwApi) => {
    let initialized2 = false;
    let internalState = {
      currentSubscriptions: {}
    };
    const builderArgs = {
      ...input,
      internalState,
      refetchQuery,
      isThisApiSliceAction
    };
    const handlers = handlerBuilders.map((build) => build(builderArgs));
    const batchedActionsHandler = buildBatchedActionsHandler(builderArgs);
    const windowEventsHandler = buildWindowEventHandler(builderArgs);
    return (next) => {
      return (action) => {
        if (!isAction(action)) {
          return next(action);
        }
        if (!initialized2) {
          initialized2 = true;
          mwApi.dispatch(api.internalActions.middlewareRegistered(apiUid));
        }
        const mwApiWithNext = {
          ...mwApi,
          next
        };
        const stateBefore = mwApi.getState();
        const [actionShouldContinue, internalProbeResult] = batchedActionsHandler(action, mwApiWithNext, stateBefore);
        let res;
        if (actionShouldContinue) {
          res = next(action);
        } else {
          res = internalProbeResult;
        }
        if (!!mwApi.getState()[reducerPath]) {
          windowEventsHandler(action, mwApiWithNext, stateBefore);
          if (isThisApiSliceAction(action) || context.hasRehydrationInfo(action)) {
            for (let handler of handlers) {
              handler(action, mwApiWithNext, stateBefore);
            }
          }
        }
        return res;
      };
    };
  };
  return {
    middleware,
    actions
  };
  function refetchQuery(querySubState, queryCacheKey, override = {}) {
    return queryThunk({
      type: "query",
      endpointName: querySubState.endpointName,
      originalArgs: querySubState.originalArgs,
      subscribe: false,
      forceRefetch: true,
      queryCacheKey,
      ...override
    });
  }
}
function safeAssign$1(target, ...args) {
  return Object.assign(target, ...args);
}
var coreModuleName = /* @__PURE__ */ Symbol();
var coreModule = ({
  createSelector: createSelector2 = createSelector$1
} = {}) => ({
  name: coreModuleName,
  init(api, {
    baseQuery,
    tagTypes,
    reducerPath,
    serializeQueryArgs,
    keepUnusedDataFor,
    refetchOnMountOrArgChange,
    refetchOnFocus,
    refetchOnReconnect,
    invalidationBehavior
  }, context) {
    enablePatches();
    const assertTagType = (tag) => {
      if (typeof process !== "undefined" && process.env.NODE_ENV === "development") {
        if (!tagTypes.includes(tag.type)) {
          console.error(`Tag type '${tag.type}' was used, but not specified in \`tagTypes\`!`);
        }
      }
      return tag;
    };
    Object.assign(api, {
      reducerPath,
      endpoints: {},
      internalActions: {
        onOnline,
        onOffline,
        onFocus,
        onFocusLost
      },
      util: {}
    });
    const {
      queryThunk,
      mutationThunk,
      patchQueryData,
      updateQueryData,
      upsertQueryData,
      prefetch,
      buildMatchThunkActions
    } = buildThunks({
      baseQuery,
      reducerPath,
      context,
      api,
      serializeQueryArgs,
      assertTagType
    });
    const {
      reducer,
      actions: sliceActions
    } = buildSlice({
      context,
      queryThunk,
      mutationThunk,
      reducerPath,
      assertTagType,
      config: {
        refetchOnFocus,
        refetchOnReconnect,
        refetchOnMountOrArgChange,
        keepUnusedDataFor,
        reducerPath,
        invalidationBehavior
      }
    });
    safeAssign$1(api.util, {
      patchQueryData,
      updateQueryData,
      upsertQueryData,
      prefetch,
      resetApiState: sliceActions.resetApiState
    });
    safeAssign$1(api.internalActions, sliceActions);
    const {
      middleware,
      actions: middlewareActions
    } = buildMiddleware({
      reducerPath,
      context,
      queryThunk,
      mutationThunk,
      api,
      assertTagType
    });
    safeAssign$1(api.util, middlewareActions);
    safeAssign$1(api, {
      reducer,
      middleware
    });
    const {
      buildQuerySelector,
      buildMutationSelector,
      selectInvalidatedBy,
      selectCachedArgsForQuery
    } = buildSelectors({
      serializeQueryArgs,
      reducerPath,
      createSelector: createSelector2
    });
    safeAssign$1(api.util, {
      selectInvalidatedBy,
      selectCachedArgsForQuery
    });
    const {
      buildInitiateQuery,
      buildInitiateMutation,
      getRunningMutationThunk,
      getRunningMutationsThunk,
      getRunningQueriesThunk,
      getRunningQueryThunk
    } = buildInitiate({
      queryThunk,
      mutationThunk,
      api,
      serializeQueryArgs,
      context
    });
    safeAssign$1(api.util, {
      getRunningMutationThunk,
      getRunningMutationsThunk,
      getRunningQueryThunk,
      getRunningQueriesThunk
    });
    return {
      name: coreModuleName,
      injectEndpoint(endpointName, definition) {
        const anyApi = api;
        anyApi.endpoints[endpointName] ??= {};
        if (isQueryDefinition$1(definition)) {
          safeAssign$1(anyApi.endpoints[endpointName], {
            name: endpointName,
            select: buildQuerySelector(endpointName, definition),
            initiate: buildInitiateQuery(endpointName, definition)
          }, buildMatchThunkActions(queryThunk, endpointName));
        } else if (isMutationDefinition$1(definition)) {
          safeAssign$1(anyApi.endpoints[endpointName], {
            name: endpointName,
            select: buildMutationSelector(),
            initiate: buildInitiateMutation(endpointName)
          }, buildMatchThunkActions(mutationThunk, endpointName));
        }
      }
    };
  }
});

// src/query/react/index.ts

// src/query/endpointDefinitions.ts
function isQueryDefinition(e) {
  return e.type === "query" /* query */;
}
function isMutationDefinition(e) {
  return e.type === "mutation" /* mutation */;
}

// src/query/tsHelpers.ts
function safeAssign(target, ...args) {
  return Object.assign(target, ...args);
}

// src/query/utils/capitalize.ts
function capitalize(str) {
  return str.replace(str[0], str[0].toUpperCase());
}

// src/query/defaultSerializeQueryArgs.ts
var cache = WeakMap ? /* @__PURE__ */ new WeakMap() : void 0;
var defaultSerializeQueryArgs = ({
  endpointName,
  queryArgs
}) => {
  let serialized = "";
  const cached = cache?.get(queryArgs);
  if (typeof cached === "string") {
    serialized = cached;
  } else {
    const stringified = JSON.stringify(queryArgs, (key, value) => {
      value = typeof value === "bigint" ? {
        $bigint: value.toString()
      } : value;
      value = isPlainObject$1(value) ? Object.keys(value).sort().reduce((acc, key2) => {
        acc[key2] = value[key2];
        return acc;
      }, {}) : value;
      return value;
    });
    if (isPlainObject$1(queryArgs)) {
      cache?.set(queryArgs, stringified);
    }
    serialized = stringified;
  }
  return `${endpointName}(${serialized})`;
};

// src/query/react/constants.ts
var UNINITIALIZED_VALUE = Symbol();
function useStableQueryArgs(queryArgs, serialize, endpointDefinition, endpointName) {
  const incoming = useMemo(() => ({
    queryArgs,
    serialized: typeof queryArgs == "object" ? serialize({
      queryArgs,
      endpointDefinition,
      endpointName
    }) : queryArgs
  }), [queryArgs, serialize, endpointDefinition, endpointName]);
  const cache2 = useRef(incoming);
  useEffect(() => {
    if (cache2.current.serialized !== incoming.serialized) {
      cache2.current = incoming;
    }
  }, [incoming]);
  return cache2.current.serialized === incoming.serialized ? cache2.current.queryArgs : queryArgs;
}
function useShallowStableValue(value) {
  const cache2 = useRef(value);
  useEffect(() => {
    if (!shallowEqual(cache2.current, value)) {
      cache2.current = value;
    }
  }, [value]);
  return shallowEqual(cache2.current, value) ? cache2.current : value;
}

// src/query/react/buildHooks.ts
var canUseDOM = () => !!(typeof window !== "undefined" && typeof window.document !== "undefined" && typeof window.document.createElement !== "undefined");
var isDOM = /* @__PURE__ */ canUseDOM();
var isRunningInReactNative = () => typeof navigator !== "undefined" && navigator.product === "ReactNative";
var isReactNative = /* @__PURE__ */ isRunningInReactNative();
var getUseIsomorphicLayoutEffect = () => isDOM || isReactNative ? useLayoutEffect : useEffect;
var useIsomorphicLayoutEffect = /* @__PURE__ */ getUseIsomorphicLayoutEffect();
var noPendingQueryStateSelector = (selected) => {
  if (selected.isUninitialized) {
    return {
      ...selected,
      isUninitialized: false,
      isFetching: true,
      isLoading: selected.data !== void 0 ? false : true,
      status: QueryStatus.pending
    };
  }
  return selected;
};
function buildHooks({
  api,
  moduleOptions: {
    batch,
    hooks: {
      useDispatch,
      useSelector,
      useStore
    },
    unstable__sideEffectsInRender,
    createSelector: createSelector2
  },
  serializeQueryArgs,
  context
}) {
  const usePossiblyImmediateEffect = unstable__sideEffectsInRender ? (cb) => cb() : useEffect;
  return {
    buildQueryHooks,
    buildMutationHook,
    usePrefetch
  };
  function queryStatePreSelector(currentState, lastResult, queryArgs) {
    if (lastResult?.endpointName && currentState.isUninitialized) {
      const {
        endpointName
      } = lastResult;
      const endpointDefinition = context.endpointDefinitions[endpointName];
      if (serializeQueryArgs({
        queryArgs: lastResult.originalArgs,
        endpointDefinition,
        endpointName
      }) === serializeQueryArgs({
        queryArgs,
        endpointDefinition,
        endpointName
      }))
        lastResult = void 0;
    }
    let data = currentState.isSuccess ? currentState.data : lastResult?.data;
    if (data === void 0)
      data = currentState.data;
    const hasData = data !== void 0;
    const isFetching = currentState.isLoading;
    const isLoading = (!lastResult || lastResult.isLoading || lastResult.isUninitialized) && !hasData && isFetching;
    const isSuccess = currentState.isSuccess || isFetching && hasData;
    return {
      ...currentState,
      data,
      currentData: currentState.data,
      isFetching,
      isLoading,
      isSuccess
    };
  }
  function usePrefetch(endpointName, defaultOptions) {
    const dispatch = useDispatch();
    const stableDefaultOptions = useShallowStableValue(defaultOptions);
    return useCallback((arg, options) => dispatch(api.util.prefetch(endpointName, arg, {
      ...stableDefaultOptions,
      ...options
    })), [endpointName, dispatch, stableDefaultOptions]);
  }
  function buildQueryHooks(name) {
    const useQuerySubscription = (arg, {
      refetchOnReconnect,
      refetchOnFocus,
      refetchOnMountOrArgChange,
      skip = false,
      pollingInterval = 0,
      skipPollingIfUnfocused = false
    } = {}) => {
      const {
        initiate
      } = api.endpoints[name];
      const dispatch = useDispatch();
      const subscriptionSelectorsRef = useRef(void 0);
      if (!subscriptionSelectorsRef.current) {
        const returnedValue = dispatch(api.internalActions.internal_getRTKQSubscriptions());
        if (process.env.NODE_ENV !== "production") {
          if (typeof returnedValue !== "object" || typeof returnedValue?.type === "string") {
            throw new Error(process.env.NODE_ENV === "production" ? formatProdErrorMessage(37) : `Warning: Middleware for RTK-Query API at reducerPath "${api.reducerPath}" has not been added to the store.
    You must add the middleware for RTK-Query to function correctly!`);
          }
        }
        subscriptionSelectorsRef.current = returnedValue;
      }
      const stableArg = useStableQueryArgs(
        skip ? skipToken : arg,
        // Even if the user provided a per-endpoint `serializeQueryArgs` with
        // a consistent return value, _here_ we want to use the default behavior
        // so we can tell if _anything_ actually changed. Otherwise, we can end up
        // with a case where the query args did change but the serialization doesn't,
        // and then we never try to initiate a refetch.
        defaultSerializeQueryArgs,
        context.endpointDefinitions[name],
        name
      );
      const stableSubscriptionOptions = useShallowStableValue({
        refetchOnReconnect,
        refetchOnFocus,
        pollingInterval,
        skipPollingIfUnfocused
      });
      const lastRenderHadSubscription = useRef(false);
      const promiseRef = useRef(void 0);
      let {
        queryCacheKey,
        requestId
      } = promiseRef.current || {};
      let currentRenderHasSubscription = false;
      if (queryCacheKey && requestId) {
        currentRenderHasSubscription = subscriptionSelectorsRef.current.isRequestSubscribed(queryCacheKey, requestId);
      }
      const subscriptionRemoved = !currentRenderHasSubscription && lastRenderHadSubscription.current;
      usePossiblyImmediateEffect(() => {
        lastRenderHadSubscription.current = currentRenderHasSubscription;
      });
      usePossiblyImmediateEffect(() => {
        if (subscriptionRemoved) {
          promiseRef.current = void 0;
        }
      }, [subscriptionRemoved]);
      usePossiblyImmediateEffect(() => {
        const lastPromise = promiseRef.current;
        if (typeof process !== "undefined" && process.env.NODE_ENV === "removeMeOnCompilation") {
          console.log(subscriptionRemoved);
        }
        if (stableArg === skipToken) {
          lastPromise?.unsubscribe();
          promiseRef.current = void 0;
          return;
        }
        const lastSubscriptionOptions = promiseRef.current?.subscriptionOptions;
        if (!lastPromise || lastPromise.arg !== stableArg) {
          lastPromise?.unsubscribe();
          const promise = dispatch(initiate(stableArg, {
            subscriptionOptions: stableSubscriptionOptions,
            forceRefetch: refetchOnMountOrArgChange
          }));
          promiseRef.current = promise;
        } else if (stableSubscriptionOptions !== lastSubscriptionOptions) {
          lastPromise.updateSubscriptionOptions(stableSubscriptionOptions);
        }
      }, [dispatch, initiate, refetchOnMountOrArgChange, stableArg, stableSubscriptionOptions, subscriptionRemoved]);
      useEffect(() => {
        return () => {
          promiseRef.current?.unsubscribe();
          promiseRef.current = void 0;
        };
      }, []);
      return useMemo(() => ({
        /**
         * A method to manually refetch data for the query
         */
        refetch: () => {
          if (!promiseRef.current)
            throw new Error(process.env.NODE_ENV === "production" ? formatProdErrorMessage(38) : "Cannot refetch a query that has not been started yet.");
          return promiseRef.current?.refetch();
        }
      }), []);
    };
    const useLazyQuerySubscription = ({
      refetchOnReconnect,
      refetchOnFocus,
      pollingInterval = 0,
      skipPollingIfUnfocused = false
    } = {}) => {
      const {
        initiate
      } = api.endpoints[name];
      const dispatch = useDispatch();
      const [arg, setArg] = useState(UNINITIALIZED_VALUE);
      const promiseRef = useRef(void 0);
      const stableSubscriptionOptions = useShallowStableValue({
        refetchOnReconnect,
        refetchOnFocus,
        pollingInterval,
        skipPollingIfUnfocused
      });
      usePossiblyImmediateEffect(() => {
        const lastSubscriptionOptions = promiseRef.current?.subscriptionOptions;
        if (stableSubscriptionOptions !== lastSubscriptionOptions) {
          promiseRef.current?.updateSubscriptionOptions(stableSubscriptionOptions);
        }
      }, [stableSubscriptionOptions]);
      const subscriptionOptionsRef = useRef(stableSubscriptionOptions);
      usePossiblyImmediateEffect(() => {
        subscriptionOptionsRef.current = stableSubscriptionOptions;
      }, [stableSubscriptionOptions]);
      const trigger = useCallback(function(arg2, preferCacheValue = false) {
        let promise;
        batch(() => {
          promiseRef.current?.unsubscribe();
          promiseRef.current = promise = dispatch(initiate(arg2, {
            subscriptionOptions: subscriptionOptionsRef.current,
            forceRefetch: !preferCacheValue
          }));
          setArg(arg2);
        });
        return promise;
      }, [dispatch, initiate]);
      useEffect(() => {
        return () => {
          promiseRef?.current?.unsubscribe();
        };
      }, []);
      useEffect(() => {
        if (arg !== UNINITIALIZED_VALUE && !promiseRef.current) {
          trigger(arg, true);
        }
      }, [arg, trigger]);
      return useMemo(() => [trigger, arg], [trigger, arg]);
    };
    const useQueryState = (arg, {
      skip = false,
      selectFromResult
    } = {}) => {
      const {
        select
      } = api.endpoints[name];
      const stableArg = useStableQueryArgs(skip ? skipToken : arg, serializeQueryArgs, context.endpointDefinitions[name], name);
      const lastValue = useRef(void 0);
      const selectDefaultResult = useMemo(() => createSelector2([select(stableArg), (_, lastResult) => lastResult, (_) => stableArg], queryStatePreSelector, {
        memoizeOptions: {
          resultEqualityCheck: shallowEqual
        }
      }), [select, stableArg]);
      const querySelector = useMemo(() => selectFromResult ? createSelector2([selectDefaultResult], selectFromResult, {
        devModeChecks: {
          identityFunctionCheck: "never"
        }
      }) : selectDefaultResult, [selectDefaultResult, selectFromResult]);
      const currentState = useSelector((state) => querySelector(state, lastValue.current), shallowEqual);
      const store = useStore();
      const newLastValue = selectDefaultResult(store.getState(), lastValue.current);
      useIsomorphicLayoutEffect(() => {
        lastValue.current = newLastValue;
      }, [newLastValue]);
      return currentState;
    };
    return {
      useQueryState,
      useQuerySubscription,
      useLazyQuerySubscription,
      useLazyQuery(options) {
        const [trigger, arg] = useLazyQuerySubscription(options);
        const queryStateResults = useQueryState(arg, {
          ...options,
          skip: arg === UNINITIALIZED_VALUE
        });
        const info = useMemo(() => ({
          lastArg: arg
        }), [arg]);
        return useMemo(() => [trigger, queryStateResults, info], [trigger, queryStateResults, info]);
      },
      useQuery(arg, options) {
        const querySubscriptionResults = useQuerySubscription(arg, options);
        const queryStateResults = useQueryState(arg, {
          selectFromResult: arg === skipToken || options?.skip ? void 0 : noPendingQueryStateSelector,
          ...options
        });
        const {
          data,
          status,
          isLoading,
          isSuccess,
          isError,
          error
        } = queryStateResults;
        useDebugValue({
          data,
          status,
          isLoading,
          isSuccess,
          isError,
          error
        });
        return useMemo(() => ({
          ...queryStateResults,
          ...querySubscriptionResults
        }), [queryStateResults, querySubscriptionResults]);
      }
    };
  }
  function buildMutationHook(name) {
    return ({
      selectFromResult,
      fixedCacheKey
    } = {}) => {
      const {
        select,
        initiate
      } = api.endpoints[name];
      const dispatch = useDispatch();
      const [promise, setPromise] = useState();
      useEffect(() => () => {
        if (!promise?.arg.fixedCacheKey) {
          promise?.reset();
        }
      }, [promise]);
      const triggerMutation = useCallback(function(arg) {
        const promise2 = dispatch(initiate(arg, {
          fixedCacheKey
        }));
        setPromise(promise2);
        return promise2;
      }, [dispatch, initiate, fixedCacheKey]);
      const {
        requestId
      } = promise || {};
      const selectDefaultResult = useMemo(() => select({
        fixedCacheKey,
        requestId: promise?.requestId
      }), [fixedCacheKey, promise, select]);
      const mutationSelector = useMemo(() => selectFromResult ? createSelector2([selectDefaultResult], selectFromResult) : selectDefaultResult, [selectFromResult, selectDefaultResult]);
      const currentState = useSelector(mutationSelector, shallowEqual);
      const originalArgs = fixedCacheKey == null ? promise?.arg.originalArgs : void 0;
      const reset = useCallback(() => {
        batch(() => {
          if (promise) {
            setPromise(void 0);
          }
          if (fixedCacheKey) {
            dispatch(api.internalActions.removeMutationResult({
              requestId,
              fixedCacheKey
            }));
          }
        });
      }, [dispatch, fixedCacheKey, promise, requestId]);
      const {
        endpointName,
        data,
        status,
        isLoading,
        isSuccess,
        isError,
        error
      } = currentState;
      useDebugValue({
        endpointName,
        data,
        status,
        isLoading,
        isSuccess,
        isError,
        error
      });
      const finalState = useMemo(() => ({
        ...currentState,
        originalArgs,
        reset
      }), [currentState, originalArgs, reset]);
      return useMemo(() => [triggerMutation, finalState], [triggerMutation, finalState]);
    };
  }
}

// src/query/utils/countObjectKeys.ts
function countObjectKeys(obj) {
  let count = 0;
  for (const _key in obj) {
    count++;
  }
  return count;
}

// src/query/react/module.ts
var reactHooksModuleName = /* @__PURE__ */ Symbol();
var reactHooksModule = ({
  batch: batch$1 = batch,
  hooks = {
    useDispatch: useDispatch,
    useSelector: useSelector,
    useStore: useStore
  },
  createSelector: createSelector2 = createSelector,
  unstable__sideEffectsInRender = false,
  ...rest
} = {}) => {
  if (process.env.NODE_ENV !== "production") {
    const hookNames = ["useDispatch", "useSelector", "useStore"];
    let warned = false;
    for (const hookName of hookNames) {
      if (countObjectKeys(rest) > 0) {
        if (rest[hookName]) {
          if (!warned) {
            console.warn("As of RTK 2.0, the hooks now need to be specified as one object, provided under a `hooks` key:\n`reactHooksModule({ hooks: { useDispatch, useSelector, useStore } })`");
            warned = true;
          }
        }
        hooks[hookName] = rest[hookName];
      }
      if (typeof hooks[hookName] !== "function") {
        throw new Error(process.env.NODE_ENV === "production" ? formatProdErrorMessage(36) : `When using custom hooks for context, all ${hookNames.length} hooks need to be provided: ${hookNames.join(", ")}.
Hook ${hookName} was either not provided or not a function.`);
      }
    }
  }
  return {
    name: reactHooksModuleName,
    init(api, {
      serializeQueryArgs
    }, context) {
      const anyApi = api;
      const {
        buildQueryHooks,
        buildMutationHook,
        usePrefetch
      } = buildHooks({
        api,
        moduleOptions: {
          batch: batch$1,
          hooks,
          unstable__sideEffectsInRender,
          createSelector: createSelector2
        },
        serializeQueryArgs,
        context
      });
      safeAssign(anyApi, {
        usePrefetch
      });
      safeAssign(context, {
        batch: batch$1
      });
      return {
        injectEndpoint(endpointName, definition) {
          if (isQueryDefinition(definition)) {
            const {
              useQuery,
              useLazyQuery,
              useLazyQuerySubscription,
              useQueryState,
              useQuerySubscription
            } = buildQueryHooks(endpointName);
            safeAssign(anyApi.endpoints[endpointName], {
              useQuery,
              useLazyQuery,
              useLazyQuerySubscription,
              useQueryState,
              useQuerySubscription
            });
            api[`use${capitalize(endpointName)}Query`] = useQuery;
            api[`useLazy${capitalize(endpointName)}Query`] = useLazyQuery;
          } else if (isMutationDefinition(definition)) {
            const useMutation = buildMutationHook(endpointName);
            safeAssign(anyApi.endpoints[endpointName], {
              useMutation
            });
            api[`use${capitalize(endpointName)}Mutation`] = useMutation;
          }
        }
      };
    }
  };
};

// src/query/react/index.ts
var createApi = /* @__PURE__ */ buildCreateApi(coreModule(), reactHooksModule());

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var _a$5;
var ErrorType;
(function (ErrorType) {
    ErrorType["REQUIRED"] = "required";
    ErrorType["EMAIL"] = "email";
    ErrorType["BIRTHDAY"] = "birthday";
    ErrorType["DATEMAXNOW"] = "dateMaxNow";
    ErrorType["DATEMINNOW"] = "dateMinNow";
    ErrorType["COUNTSYMBOL"] = "countSymbol";
    ErrorType["MIN"] = "min";
})(ErrorType || (ErrorType = {}));
(_a$5 = {},
    _a$5[ErrorType.REQUIRED] = 'Поле обязательное для заполнения',
    _a$5[ErrorType.EMAIL] = 'Введите корректное значение E-mail',
    _a$5[ErrorType.BIRTHDAY] = 'Дата имеет неверный формат. Вместо этого используйте один из этих форматов: ГГГГ-ММ-ДД.',
    _a$5[ErrorType.DATEMAXNOW] = 'Дата не может быть больше чем {0}',
    _a$5[ErrorType.DATEMINNOW] = 'Дата не может быть меньше чем {0}',
    _a$5[ErrorType.COUNTSYMBOL] = 'Количество символов должно составлять: {0} символов',
    _a$5[ErrorType.MIN] = 'Минимальное значение {0}',
    _a$5);

var SummerRooms;
(function (SummerRooms) {
    SummerRooms[SummerRooms["UNKNOWN"] = -1] = "UNKNOWN";
    SummerRooms[SummerRooms["NO"] = 0] = "NO";
    SummerRooms[SummerRooms["BALCONY"] = 1] = "BALCONY";
    SummerRooms[SummerRooms["LOGGIA"] = 2] = "LOGGIA";
})(SummerRooms || (SummerRooms = {}));
var RenovationTypes;
(function (RenovationTypes) {
    RenovationTypes[RenovationTypes["UNKNOWN"] = -1] = "UNKNOWN";
    RenovationTypes[RenovationTypes["NO"] = 0] = "NO";
    RenovationTypes[RenovationTypes["COSMETIC"] = 1] = "COSMETIC";
    RenovationTypes[RenovationTypes["EURO"] = 2] = "EURO";
    RenovationTypes[RenovationTypes["DESIGN"] = 3] = "DESIGN";
    RenovationTypes[RenovationTypes["PRECLEAN"] = 4] = "PRECLEAN";
    RenovationTypes[RenovationTypes["ROUGH"] = 5] = "ROUGH";
})(RenovationTypes || (RenovationTypes = {}));
var HouseType;
(function (HouseType) {
    HouseType[HouseType["UNKNOWN"] = -1] = "UNKNOWN";
    HouseType[HouseType["NEW"] = 1] = "NEW";
    HouseType[HouseType["RESALE"] = 2] = "RESALE";
})(HouseType || (HouseType = {}));
var WindowsView;
(function (WindowsView) {
    WindowsView[WindowsView["UNKNOWN"] = -1] = "UNKNOWN";
    WindowsView[WindowsView["YARD"] = 0] = "YARD";
    WindowsView[WindowsView["STREET"] = 1] = "STREET";
})(WindowsView || (WindowsView = {}));
var WallTypes;
(function (WallTypes) {
    WallTypes["UNKNOWN"] = "unknown";
    WallTypes["BRICK"] = "brick";
    WallTypes["PANEL"] = "panel";
    WallTypes["BLOCK"] = "block";
    WallTypes["MONOLITH"] = "monolith";
    WallTypes["MONOLITH_BRICK"] = "monolith_brick";
    WallTypes["WOODEN"] = "wooden";
})(WallTypes || (WallTypes = {}));

var _a$4;
(_a$4 = {},
    _a$4[HouseType.UNKNOWN] = 'Неизвестно',
    _a$4[HouseType.NEW] = 'Новостройка',
    _a$4[HouseType.RESALE] = 'Вторичка',
    _a$4);

var _a$3;
var Pattern;
(function (Pattern) {
    Pattern["EMAIL"] = "email";
})(Pattern || (Pattern = {}));
(_a$3 = {},
    _a$3[Pattern.EMAIL] = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
    _a$3);

var _a$2;
(_a$2 = {},
    _a$2[RenovationTypes.UNKNOWN] = 'Неизвестно',
    _a$2[RenovationTypes.NO] = 'Без ремонта',
    _a$2[RenovationTypes.COSMETIC] = 'Под чистовую',
    _a$2[RenovationTypes.EURO] = 'Евроремонт',
    _a$2[RenovationTypes.DESIGN] = 'Дизайнерский',
    _a$2[RenovationTypes.PRECLEAN] = 'Предчистовой',
    _a$2[RenovationTypes.ROUGH] = 'Черновой',
    _a$2);

var _a$1;
(_a$1 = {},
    _a$1[WallTypes.UNKNOWN] = 'Неизвестно',
    _a$1[WallTypes.BRICK] = 'Кирпич',
    _a$1[WallTypes.PANEL] = 'Панель',
    _a$1[WallTypes.BLOCK] = 'Блок',
    _a$1[WallTypes.MONOLITH] = 'Монолит',
    _a$1[WallTypes.MONOLITH_BRICK] = 'Монолит-кирпич',
    _a$1[WallTypes.WOODEN] = 'Дерево',
    _a$1);

var _a;
(_a = {},
    _a[WindowsView.UNKNOWN] = 'Неизвестно',
    _a[WindowsView.STREET] = 'На улицу',
    _a[WindowsView.YARD] = 'Во двор',
    _a);

var TOKEN_KEY = 'auth.token';

function getValueInStorage(key) {
    var value = localStorage.getItem(key);
    if (value) {
        return value;
    }
    return sessionStorage.getItem(key);
}

function saveInStorage(key, value, isLocalStorage) {
    if (isLocalStorage === void 0) { isLocalStorage = false; }
    if (Array.isArray(key) && Array.isArray(value)) {
        key.forEach(function (k, i) {
            return isLocalStorage
                ? localStorage.setItem(k, value[i])
                : sessionStorage.setItem(k, value[i]);
        });
    }
    else if (typeof value === 'string') {
        isLocalStorage
            ? localStorage.setItem(key, value)
            : sessionStorage.setItem(key, value);
    }
}

function removeInStorage(key) {
    [localStorage, sessionStorage].forEach(function (s) { return s.removeItem(key); });
}

var initialState = {
    token: getValueInStorage(TOKEN_KEY),
};
var slice = createSlice({
    name: 'token',
    initialState: initialState,
    reducers: {
        setToken: function (state, action) {
            state.token = action.payload;
        },
        removeToken: function (state) {
            state.token = null;
        },
    },
});
slice.reducer; var tokenActions = slice.actions;

var baseUrl = process.env.REACT_APP_SERVER_ENDPOINT
    ? process.env.REACT_APP_SERVER_ENDPOINT
    : '/';
var baseUrlWithProtocol = baseUrl.startsWith('http')
    ? baseUrl
    : 'http://' + baseUrl;
var statusCodsRefresh = [401, 403];
var baseQueryToken = fetchBaseQuery({
    baseUrl: baseUrlWithProtocol,
    prepareHeaders: function (headers) {
        var token = getValueInStorage(TOKEN_KEY);
        if (token) {
            headers.set('x-authorization', "JWT ".concat(token));
        }
        return headers;
    },
});
var baseQuery = function (args, api, extraOptions) { return __awaiter(void 0, void 0, void 0, function () {
    var result, refreshResult, keepLoggedIn;
    var _a, _b, _c;
    return __generator(this, function (_d) {
        switch (_d.label) {
            case 0: return [4 /*yield*/, baseQueryToken(args, api, extraOptions)];
            case 1:
                result = _d.sent();
                if (!statusCodsRefresh.includes(Number((_a = result.error) === null || _a === void 0 ? void 0 : _a.status))) return [3 /*break*/, 5];
                return [4 /*yield*/, baseQueryToken({
                        url: 'auth/token/refresh/',
                        method: 'POST',
                        body: {
                            token: (_c = (_b = result.meta) === null || _b === void 0 ? void 0 : _b.request.headers.get('x-authorization')) === null || _c === void 0 ? void 0 : _c.split(' ')[1],
                        },
                    }, api, extraOptions)];
            case 2:
                refreshResult = _d.sent();
                if (!refreshResult.data) return [3 /*break*/, 4];
                keepLoggedIn = Boolean(localStorage.getItem('auth.keepLoggedIn'));
                api.dispatch(tokenActions.setToken(refreshResult.data.token));
                saveInStorage(TOKEN_KEY, refreshResult.data.token, keepLoggedIn);
                return [4 /*yield*/, baseQueryToken(args, api, extraOptions)];
            case 3:
                result = _d.sent();
                return [3 /*break*/, 5];
            case 4:
                api.dispatch(tokenActions.removeToken());
                removeInStorage(TOKEN_KEY);
                _d.label = 5;
            case 5: return [2 /*return*/, result];
        }
    });
}); };

var generatedApi = createApi({
    baseQuery: baseQuery,
    endpoints: function () { return ({}); },
    reducerPath: 'api',
});

var injectedRtkApi$1 = generatedApi.injectEndpoints({
    endpoints: function (build) { return ({
        apiV1AppraisersList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/appraisers/",
                params: {
                    evaluating_company: queryArg.evaluatingCompany,
                    signer: queryArg.signer,
                },
            }); },
        }),
        apiV1AppraisersRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/appraisers/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1AppraisersEvaluatingCompaniesList: build.query({
            query: function () { return ({ url: "/api/v1/appraisers/evaluating-companies/" }); },
        }),
        apiV1BanksList: build.query({
            query: function () { return ({ url: "/api/v1/banks/" }); },
        }),
        apiV1BanksRetrieve: build.query({
            query: function (queryArg) { return ({ url: "/api/v1/banks/".concat(queryArg.id, "/") }); },
        }),
        apiV1BuildingsHouseRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/buildings/house/".concat(queryArg.houseId),
            }); },
        }),
        apiV1BuildingsSearchRetrieve: build.query({
            query: function () { return ({ url: "/api/v1/buildings/search" }); },
        }),
        apiV1CitiesList: build.query({
            query: function () { return ({ url: "/api/v1/cities/" }); },
        }),
        apiV1CitiesRetrieve: build.query({
            query: function (queryArg) { return ({ url: "/api/v1/cities/".concat(queryArg.id, "/") }); },
        }),
        apiV1CrmAssessmentObjectList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/assessment-object/",
                params: { page: queryArg.page, page_size: queryArg.pageSize },
            }); },
        }),
        apiV1CrmAssessmentObjectCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/assessment-object/",
                method: 'POST',
                body: queryArg.assessmentObject,
            }); },
        }),
        apiV1CrmAssessmentObjectRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/assessment-object/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1CrmAssessmentObjectUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/assessment-object/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.assessmentObject,
            }); },
        }),
        apiV1CrmAssessmentObjectPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/assessment-object/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedAssessmentObject,
            }); },
        }),
        apiV1CrmAssessmentObjectDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/assessment-object/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1CrmCustomerOrdersList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customer/orders/",
                params: {
                    ordering: queryArg.ordering,
                    page: queryArg.page,
                    page_size: queryArg.pageSize,
                },
            }); },
        }),
        apiV1CrmCustomerOrdersRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customer/orders/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1CrmCustomerOrdersUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customer/orders/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.customerOrderWrite,
            }); },
        }),
        apiV1CrmCustomerOrdersPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customer/orders/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedCustomerOrderWrite,
            }); },
        }),
        apiV1PortalCustomerOrdersCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/portal/new-order/",
                method: 'POST',
                body: queryArg.customerOrderCreate,
            }); },
        }),
        apiV1CrmCustomersList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customers/",
                params: { page: queryArg.page, page_size: queryArg.pageSize },
            }); },
        }),
        apiV1CrmCustomersCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customers/",
                method: 'POST',
                body: queryArg.customerWrite,
            }); },
        }),
        apiV1CrmCustomersRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customers/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1CrmCustomersUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customers/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.customerWrite,
            }); },
        }),
        apiV1CrmCustomersPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customers/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedCustomerWrite,
            }); },
        }),
        apiV1CrmCustomersDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/customers/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1CrmDocumentsList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents/",
                params: { page: queryArg.page, page_size: queryArg.pageSize },
            }); },
        }),
        apiV1CrmDocumentsCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents/",
                method: 'POST',
                body: queryArg.document,
            }); },
        }),
        apiV1CrmDocumentsGroupsList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents-groups/",
                params: { page: queryArg.page, page_size: queryArg.pageSize },
            }); },
        }),
        apiV1CrmDocumentsGroupsCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents-groups/",
                method: 'POST',
                body: queryArg.documentsGroup,
            }); },
        }),
        apiV1CrmDocumentsGroupsRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents-groups/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1CrmDocumentsGroupsUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents-groups/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.documentsGroup,
            }); },
        }),
        apiV1CrmDocumentsGroupsPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents-groups/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedDocumentsGroup,
            }); },
        }),
        apiV1CrmDocumentsGroupsDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents-groups/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1CrmDocumentsRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1CrmDocumentsUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.document,
            }); },
        }),
        apiV1CrmDocumentsPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedDocument,
            }); },
        }),
        apiV1CrmDocumentsDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/documents/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1CrmOrdersList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/",
                params: {
                    ordering: queryArg.ordering,
                    page: queryArg.page,
                    page_size: queryArg.pageSize,
                    query: queryArg.query,
                    status: queryArg.status,
                },
            }); },
        }),
        apiV1CrmOrdersCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/",
                method: 'POST',
                body: queryArg.orderCreate,
            }); },
        }),
        apiV1CrmOrdersRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1CrmOrdersUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.orderCreate,
            }); },
        }),
        apiV1CrmOrdersPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedOrderCreate,
            }); },
        }),
        apiV1CrmOrdersDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1CrmOrdersSendCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/".concat(queryArg.id, "/send/"),
                method: 'POST',
                body: queryArg.order,
            }); },
        }),
        apiV1CrmOrdersSignCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/".concat(queryArg.id, "/sign/"),
                method: 'POST',
                body: queryArg.order,
            }); },
        }),
        apiV1CrmOrdersStartCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/".concat(queryArg.id, "/start/"),
                method: 'POST',
                body: queryArg.order,
            }); },
        }),
        apiV1CrmOrdersNextRetrieve: build.query({
            query: function () { return ({ url: "/api/v1/crm/orders/next/" }); },
        }),
        apiV1CrmOrdersOwnersCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/owners/",
                method: 'POST',
                body: queryArg.owner,
            }); },
        }),
        apiV1CrmOrdersOwnersUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/owners/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.owner,
            }); },
        }),
        apiV1CrmOrdersOwnersPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/owners/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedOwner,
            }); },
        }),
        apiV1CrmOrdersOwnersDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/crm/orders/owners/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1InspectionsList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/",
                params: {
                    ordering: queryArg.ordering,
                    page: queryArg.page,
                    page_size: queryArg.pageSize,
                    status: queryArg.status,
                },
            }); },
        }),
        apiV1InspectionsCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/",
                method: 'POST',
                body: queryArg.inspection,
            }); },
        }),
        apiV1InspectionsRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1InspectionsUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.inspection,
            }); },
        }),
        apiV1InspectionsPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedInspection,
            }); },
        }),
        apiV1InspectionsDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1InspectionsPhotoGroupsList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photo-groups/",
                params: { page: queryArg.page, page_size: queryArg.pageSize },
            }); },
        }),
        apiV1InspectionsPhotoGroupsCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photo-groups/",
                method: 'POST',
                body: queryArg.photoGroup,
            }); },
        }),
        apiV1InspectionsPhotoGroupsRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photo-groups/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1InspectionsPhotoGroupsUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photo-groups/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.photoGroup,
            }); },
        }),
        apiV1InspectionsPhotoGroupsPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photo-groups/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedPhotoGroup,
            }); },
        }),
        apiV1InspectionsPhotoGroupsDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photo-groups/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1InspectionsPhotosCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photos/",
                method: 'POST',
                body: queryArg.photo,
            }); },
        }),
        apiV1InspectionsPhotosUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photos/".concat(queryArg.id, "/"),
                method: 'PUT',
                body: queryArg.photo,
            }); },
        }),
        apiV1InspectionsPhotosPartialUpdate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photos/".concat(queryArg.id, "/"),
                method: 'PATCH',
                body: queryArg.patchedPhoto,
            }); },
        }),
        apiV1InspectionsPhotosDestroy: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/inspections/photos/".concat(queryArg.id, "/"),
                method: 'DELETE',
            }); },
        }),
        apiV1PortalAddressAutocompleteRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/portal/address/autocomplete",
                params: { limit: queryArg.limit, query: queryArg.query },
            }); },
        }),
        apiV1ReportsList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/reports/",
                params: {
                    ordering: queryArg.ordering,
                    page: queryArg.page,
                    page_size: queryArg.pageSize,
                    status: queryArg.status,
                },
            }); },
        }),
        apiV1ReportsRetrieve: build.query({
            query: function (queryArg) { return ({ url: "/api/v1/reports/".concat(queryArg.id, "/") }); },
        }),
        apiV1ReportsGenerateCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/reports/".concat(queryArg.id, "/generate/"),
                method: 'POST',
            }); },
        }),
        apiV1ReportsRefillCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/reports/".concat(queryArg.id, "/refill/"),
                method: 'POST',
            }); },
        }),
        apiV1ReportsSignCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/reports/".concat(queryArg.id, "/sign/"),
                method: 'POST',
                body: queryArg.report,
            }); },
        }),
        apiV1ReportsFilesCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/reports/files/",
                method: 'POST',
                body: queryArg.reportFile,
            }); },
        }),
        apiV1ReportsFilesRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/reports/files/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1ReportsTemplatesList: build.query({
            query: function () { return ({ url: "/api/v1/reports/templates/" }); },
        }),
        apiV1ResidentialApartmentsList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/residential/apartments/",
                params: { building: queryArg.building },
            }); },
        }),
        apiV1ResidentialApartmentsRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/residential/apartments/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1ResidentialBuildingsList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/residential/buildings/",
                params: { complex: queryArg.complex },
            }); },
        }),
        apiV1ResidentialBuildingsRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/residential/buildings/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1ResidentialComplexesList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/residential/complexes/",
                params: { city: queryArg.city },
            }); },
        }),
        apiV1ResidentialComplexesRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/residential/complexes/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1ResidentialDevelopersList: build.query({
            query: function () { return ({ url: "/api/v1/residential/developers/" }); },
        }),
        apiV1ResidentialDevelopersRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/residential/developers/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1ResidentialEntrancesList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/residential/entrances/",
                params: { building: queryArg.building },
            }); },
        }),
        apiV1SelectionsList: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/selections/",
                params: {
                    ordering: queryArg.ordering,
                    page: queryArg.page,
                    page_size: queryArg.pageSize,
                    status: queryArg.status,
                },
            }); },
        }),
        apiV1SelectionsCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/selections/",
                method: 'POST',
                body: queryArg.createSelection,
            }); },
        }),
        apiV1SelectionsRetrieve: build.query({
            query: function (queryArg) { return ({
                url: "/api/v1/selections/".concat(queryArg.id, "/"),
            }); },
        }),
        apiV1UsersMeRetrieve: build.query({
            query: function () { return ({ url: "/api/v1/users/me/" }); },
        }),
    }); },
    overrideExisting: false,
});
var useApiV1AppraisersListQuery = injectedRtkApi$1.useApiV1AppraisersListQuery, useLazyApiV1AppraisersListQuery = injectedRtkApi$1.useLazyApiV1AppraisersListQuery, useApiV1AppraisersRetrieveQuery = injectedRtkApi$1.useApiV1AppraisersRetrieveQuery, useLazyApiV1AppraisersRetrieveQuery = injectedRtkApi$1.useLazyApiV1AppraisersRetrieveQuery, useApiV1AppraisersEvaluatingCompaniesListQuery = injectedRtkApi$1.useApiV1AppraisersEvaluatingCompaniesListQuery, useLazyApiV1AppraisersEvaluatingCompaniesListQuery = injectedRtkApi$1.useLazyApiV1AppraisersEvaluatingCompaniesListQuery, useApiV1BanksListQuery = injectedRtkApi$1.useApiV1BanksListQuery, useLazyApiV1BanksListQuery = injectedRtkApi$1.useLazyApiV1BanksListQuery, useApiV1BanksRetrieveQuery = injectedRtkApi$1.useApiV1BanksRetrieveQuery, useLazyApiV1BanksRetrieveQuery = injectedRtkApi$1.useLazyApiV1BanksRetrieveQuery, useApiV1BuildingsHouseRetrieveQuery = injectedRtkApi$1.useApiV1BuildingsHouseRetrieveQuery, useLazyApiV1BuildingsHouseRetrieveQuery = injectedRtkApi$1.useLazyApiV1BuildingsHouseRetrieveQuery, useApiV1BuildingsSearchRetrieveQuery = injectedRtkApi$1.useApiV1BuildingsSearchRetrieveQuery, useLazyApiV1BuildingsSearchRetrieveQuery = injectedRtkApi$1.useLazyApiV1BuildingsSearchRetrieveQuery, useApiV1CitiesListQuery = injectedRtkApi$1.useApiV1CitiesListQuery, useLazyApiV1CitiesListQuery = injectedRtkApi$1.useLazyApiV1CitiesListQuery, useApiV1CitiesRetrieveQuery = injectedRtkApi$1.useApiV1CitiesRetrieveQuery, useLazyApiV1CitiesRetrieveQuery = injectedRtkApi$1.useLazyApiV1CitiesRetrieveQuery, useApiV1CrmAssessmentObjectListQuery = injectedRtkApi$1.useApiV1CrmAssessmentObjectListQuery, useLazyApiV1CrmAssessmentObjectListQuery = injectedRtkApi$1.useLazyApiV1CrmAssessmentObjectListQuery, useApiV1CrmAssessmentObjectCreateMutation = injectedRtkApi$1.useApiV1CrmAssessmentObjectCreateMutation, useApiV1CrmAssessmentObjectRetrieveQuery = injectedRtkApi$1.useApiV1CrmAssessmentObjectRetrieveQuery, useLazyApiV1CrmAssessmentObjectRetrieveQuery = injectedRtkApi$1.useLazyApiV1CrmAssessmentObjectRetrieveQuery, useApiV1CrmAssessmentObjectUpdateMutation = injectedRtkApi$1.useApiV1CrmAssessmentObjectUpdateMutation, useApiV1CrmAssessmentObjectPartialUpdateMutation = injectedRtkApi$1.useApiV1CrmAssessmentObjectPartialUpdateMutation, useApiV1CrmAssessmentObjectDestroyMutation = injectedRtkApi$1.useApiV1CrmAssessmentObjectDestroyMutation, useApiV1CrmCustomerOrdersListQuery = injectedRtkApi$1.useApiV1CrmCustomerOrdersListQuery, useLazyApiV1CrmCustomerOrdersListQuery = injectedRtkApi$1.useLazyApiV1CrmCustomerOrdersListQuery, useApiV1CrmCustomerOrdersRetrieveQuery = injectedRtkApi$1.useApiV1CrmCustomerOrdersRetrieveQuery, useLazyApiV1CrmCustomerOrdersRetrieveQuery = injectedRtkApi$1.useLazyApiV1CrmCustomerOrdersRetrieveQuery, useApiV1CrmCustomerOrdersUpdateMutation = injectedRtkApi$1.useApiV1CrmCustomerOrdersUpdateMutation, useApiV1CrmCustomerOrdersPartialUpdateMutation = injectedRtkApi$1.useApiV1CrmCustomerOrdersPartialUpdateMutation, useApiV1PortalCustomerOrdersCreateMutation = injectedRtkApi$1.useApiV1PortalCustomerOrdersCreateMutation, useApiV1CrmCustomersListQuery = injectedRtkApi$1.useApiV1CrmCustomersListQuery, useLazyApiV1CrmCustomersListQuery = injectedRtkApi$1.useLazyApiV1CrmCustomersListQuery, useApiV1CrmCustomersCreateMutation = injectedRtkApi$1.useApiV1CrmCustomersCreateMutation, useApiV1CrmCustomersRetrieveQuery = injectedRtkApi$1.useApiV1CrmCustomersRetrieveQuery, useLazyApiV1CrmCustomersRetrieveQuery = injectedRtkApi$1.useLazyApiV1CrmCustomersRetrieveQuery, useApiV1CrmCustomersUpdateMutation = injectedRtkApi$1.useApiV1CrmCustomersUpdateMutation, useApiV1CrmCustomersPartialUpdateMutation = injectedRtkApi$1.useApiV1CrmCustomersPartialUpdateMutation, useApiV1CrmCustomersDestroyMutation = injectedRtkApi$1.useApiV1CrmCustomersDestroyMutation, useApiV1CrmDocumentsListQuery = injectedRtkApi$1.useApiV1CrmDocumentsListQuery, useLazyApiV1CrmDocumentsListQuery = injectedRtkApi$1.useLazyApiV1CrmDocumentsListQuery, useApiV1CrmDocumentsCreateMutation = injectedRtkApi$1.useApiV1CrmDocumentsCreateMutation, useApiV1CrmDocumentsGroupsListQuery = injectedRtkApi$1.useApiV1CrmDocumentsGroupsListQuery, useLazyApiV1CrmDocumentsGroupsListQuery = injectedRtkApi$1.useLazyApiV1CrmDocumentsGroupsListQuery, useApiV1CrmDocumentsGroupsCreateMutation = injectedRtkApi$1.useApiV1CrmDocumentsGroupsCreateMutation, useApiV1CrmDocumentsGroupsRetrieveQuery = injectedRtkApi$1.useApiV1CrmDocumentsGroupsRetrieveQuery, useLazyApiV1CrmDocumentsGroupsRetrieveQuery = injectedRtkApi$1.useLazyApiV1CrmDocumentsGroupsRetrieveQuery, useApiV1CrmDocumentsGroupsUpdateMutation = injectedRtkApi$1.useApiV1CrmDocumentsGroupsUpdateMutation, useApiV1CrmDocumentsGroupsPartialUpdateMutation = injectedRtkApi$1.useApiV1CrmDocumentsGroupsPartialUpdateMutation, useApiV1CrmDocumentsGroupsDestroyMutation = injectedRtkApi$1.useApiV1CrmDocumentsGroupsDestroyMutation, useApiV1CrmDocumentsRetrieveQuery = injectedRtkApi$1.useApiV1CrmDocumentsRetrieveQuery, useLazyApiV1CrmDocumentsRetrieveQuery = injectedRtkApi$1.useLazyApiV1CrmDocumentsRetrieveQuery, useApiV1CrmDocumentsUpdateMutation = injectedRtkApi$1.useApiV1CrmDocumentsUpdateMutation, useApiV1CrmDocumentsPartialUpdateMutation = injectedRtkApi$1.useApiV1CrmDocumentsPartialUpdateMutation, useApiV1CrmDocumentsDestroyMutation = injectedRtkApi$1.useApiV1CrmDocumentsDestroyMutation, useApiV1CrmOrdersListQuery = injectedRtkApi$1.useApiV1CrmOrdersListQuery, useLazyApiV1CrmOrdersListQuery = injectedRtkApi$1.useLazyApiV1CrmOrdersListQuery, useApiV1CrmOrdersCreateMutation = injectedRtkApi$1.useApiV1CrmOrdersCreateMutation, useApiV1CrmOrdersRetrieveQuery = injectedRtkApi$1.useApiV1CrmOrdersRetrieveQuery, useLazyApiV1CrmOrdersRetrieveQuery = injectedRtkApi$1.useLazyApiV1CrmOrdersRetrieveQuery, useApiV1CrmOrdersUpdateMutation = injectedRtkApi$1.useApiV1CrmOrdersUpdateMutation, useApiV1CrmOrdersPartialUpdateMutation = injectedRtkApi$1.useApiV1CrmOrdersPartialUpdateMutation, useApiV1CrmOrdersDestroyMutation = injectedRtkApi$1.useApiV1CrmOrdersDestroyMutation, useApiV1CrmOrdersSendCreateMutation = injectedRtkApi$1.useApiV1CrmOrdersSendCreateMutation, useApiV1CrmOrdersSignCreateMutation = injectedRtkApi$1.useApiV1CrmOrdersSignCreateMutation, useApiV1CrmOrdersStartCreateMutation = injectedRtkApi$1.useApiV1CrmOrdersStartCreateMutation, useApiV1CrmOrdersNextRetrieveQuery = injectedRtkApi$1.useApiV1CrmOrdersNextRetrieveQuery, useLazyApiV1CrmOrdersNextRetrieveQuery = injectedRtkApi$1.useLazyApiV1CrmOrdersNextRetrieveQuery, useApiV1CrmOrdersOwnersCreateMutation = injectedRtkApi$1.useApiV1CrmOrdersOwnersCreateMutation, useApiV1CrmOrdersOwnersUpdateMutation = injectedRtkApi$1.useApiV1CrmOrdersOwnersUpdateMutation, useApiV1CrmOrdersOwnersPartialUpdateMutation = injectedRtkApi$1.useApiV1CrmOrdersOwnersPartialUpdateMutation, useApiV1CrmOrdersOwnersDestroyMutation = injectedRtkApi$1.useApiV1CrmOrdersOwnersDestroyMutation, useApiV1InspectionsListQuery = injectedRtkApi$1.useApiV1InspectionsListQuery, useLazyApiV1InspectionsListQuery = injectedRtkApi$1.useLazyApiV1InspectionsListQuery, useApiV1InspectionsCreateMutation = injectedRtkApi$1.useApiV1InspectionsCreateMutation, useApiV1InspectionsRetrieveQuery = injectedRtkApi$1.useApiV1InspectionsRetrieveQuery, useLazyApiV1InspectionsRetrieveQuery = injectedRtkApi$1.useLazyApiV1InspectionsRetrieveQuery, useApiV1InspectionsUpdateMutation = injectedRtkApi$1.useApiV1InspectionsUpdateMutation, useApiV1InspectionsPartialUpdateMutation = injectedRtkApi$1.useApiV1InspectionsPartialUpdateMutation, useApiV1InspectionsDestroyMutation = injectedRtkApi$1.useApiV1InspectionsDestroyMutation, useApiV1InspectionsPhotoGroupsListQuery = injectedRtkApi$1.useApiV1InspectionsPhotoGroupsListQuery, useLazyApiV1InspectionsPhotoGroupsListQuery = injectedRtkApi$1.useLazyApiV1InspectionsPhotoGroupsListQuery, useApiV1InspectionsPhotoGroupsCreateMutation = injectedRtkApi$1.useApiV1InspectionsPhotoGroupsCreateMutation, useApiV1InspectionsPhotoGroupsRetrieveQuery = injectedRtkApi$1.useApiV1InspectionsPhotoGroupsRetrieveQuery, useLazyApiV1InspectionsPhotoGroupsRetrieveQuery = injectedRtkApi$1.useLazyApiV1InspectionsPhotoGroupsRetrieveQuery, useApiV1InspectionsPhotoGroupsUpdateMutation = injectedRtkApi$1.useApiV1InspectionsPhotoGroupsUpdateMutation, useApiV1InspectionsPhotoGroupsPartialUpdateMutation = injectedRtkApi$1.useApiV1InspectionsPhotoGroupsPartialUpdateMutation, useApiV1InspectionsPhotoGroupsDestroyMutation = injectedRtkApi$1.useApiV1InspectionsPhotoGroupsDestroyMutation, useApiV1InspectionsPhotosCreateMutation = injectedRtkApi$1.useApiV1InspectionsPhotosCreateMutation, useApiV1InspectionsPhotosUpdateMutation = injectedRtkApi$1.useApiV1InspectionsPhotosUpdateMutation, useApiV1InspectionsPhotosPartialUpdateMutation = injectedRtkApi$1.useApiV1InspectionsPhotosPartialUpdateMutation, useApiV1InspectionsPhotosDestroyMutation = injectedRtkApi$1.useApiV1InspectionsPhotosDestroyMutation, useApiV1PortalAddressAutocompleteRetrieveQuery = injectedRtkApi$1.useApiV1PortalAddressAutocompleteRetrieveQuery, useLazyApiV1PortalAddressAutocompleteRetrieveQuery = injectedRtkApi$1.useLazyApiV1PortalAddressAutocompleteRetrieveQuery, useApiV1ReportsListQuery = injectedRtkApi$1.useApiV1ReportsListQuery, useLazyApiV1ReportsListQuery = injectedRtkApi$1.useLazyApiV1ReportsListQuery, useApiV1ReportsRetrieveQuery = injectedRtkApi$1.useApiV1ReportsRetrieveQuery, useLazyApiV1ReportsRetrieveQuery = injectedRtkApi$1.useLazyApiV1ReportsRetrieveQuery, useApiV1ReportsGenerateCreateMutation = injectedRtkApi$1.useApiV1ReportsGenerateCreateMutation, useApiV1ReportsRefillCreateMutation = injectedRtkApi$1.useApiV1ReportsRefillCreateMutation, useApiV1ReportsSignCreateMutation = injectedRtkApi$1.useApiV1ReportsSignCreateMutation, useApiV1ReportsFilesCreateMutation = injectedRtkApi$1.useApiV1ReportsFilesCreateMutation, useApiV1ReportsFilesRetrieveQuery = injectedRtkApi$1.useApiV1ReportsFilesRetrieveQuery, useLazyApiV1ReportsFilesRetrieveQuery = injectedRtkApi$1.useLazyApiV1ReportsFilesRetrieveQuery, useApiV1ReportsTemplatesListQuery = injectedRtkApi$1.useApiV1ReportsTemplatesListQuery, useLazyApiV1ReportsTemplatesListQuery = injectedRtkApi$1.useLazyApiV1ReportsTemplatesListQuery, useApiV1ResidentialApartmentsListQuery = injectedRtkApi$1.useApiV1ResidentialApartmentsListQuery, useLazyApiV1ResidentialApartmentsListQuery = injectedRtkApi$1.useLazyApiV1ResidentialApartmentsListQuery, useApiV1ResidentialApartmentsRetrieveQuery = injectedRtkApi$1.useApiV1ResidentialApartmentsRetrieveQuery, useLazyApiV1ResidentialApartmentsRetrieveQuery = injectedRtkApi$1.useLazyApiV1ResidentialApartmentsRetrieveQuery, useApiV1ResidentialBuildingsListQuery = injectedRtkApi$1.useApiV1ResidentialBuildingsListQuery, useLazyApiV1ResidentialBuildingsListQuery = injectedRtkApi$1.useLazyApiV1ResidentialBuildingsListQuery, useApiV1ResidentialBuildingsRetrieveQuery = injectedRtkApi$1.useApiV1ResidentialBuildingsRetrieveQuery, useLazyApiV1ResidentialBuildingsRetrieveQuery = injectedRtkApi$1.useLazyApiV1ResidentialBuildingsRetrieveQuery, useApiV1ResidentialComplexesListQuery = injectedRtkApi$1.useApiV1ResidentialComplexesListQuery, useLazyApiV1ResidentialComplexesListQuery = injectedRtkApi$1.useLazyApiV1ResidentialComplexesListQuery, useApiV1ResidentialComplexesRetrieveQuery = injectedRtkApi$1.useApiV1ResidentialComplexesRetrieveQuery, useLazyApiV1ResidentialComplexesRetrieveQuery = injectedRtkApi$1.useLazyApiV1ResidentialComplexesRetrieveQuery, useApiV1ResidentialDevelopersListQuery = injectedRtkApi$1.useApiV1ResidentialDevelopersListQuery, useLazyApiV1ResidentialDevelopersListQuery = injectedRtkApi$1.useLazyApiV1ResidentialDevelopersListQuery, useApiV1ResidentialDevelopersRetrieveQuery = injectedRtkApi$1.useApiV1ResidentialDevelopersRetrieveQuery, useLazyApiV1ResidentialDevelopersRetrieveQuery = injectedRtkApi$1.useLazyApiV1ResidentialDevelopersRetrieveQuery, useApiV1ResidentialEntrancesListQuery = injectedRtkApi$1.useApiV1ResidentialEntrancesListQuery, useLazyApiV1ResidentialEntrancesListQuery = injectedRtkApi$1.useLazyApiV1ResidentialEntrancesListQuery, useApiV1SelectionsListQuery = injectedRtkApi$1.useApiV1SelectionsListQuery, useLazyApiV1SelectionsListQuery = injectedRtkApi$1.useLazyApiV1SelectionsListQuery, useApiV1SelectionsCreateMutation = injectedRtkApi$1.useApiV1SelectionsCreateMutation, useApiV1SelectionsRetrieveQuery = injectedRtkApi$1.useApiV1SelectionsRetrieveQuery, useLazyApiV1SelectionsRetrieveQuery = injectedRtkApi$1.useLazyApiV1SelectionsRetrieveQuery, useApiV1UsersMeRetrieveQuery = injectedRtkApi$1.useApiV1UsersMeRetrieveQuery, useLazyApiV1UsersMeRetrieveQuery = injectedRtkApi$1.useLazyApiV1UsersMeRetrieveQuery;

var injectedRtkApi = generatedApi.injectEndpoints({
    endpoints: function (build) { return ({
        filesCreate: build.mutation({
            query: function (fd) { return ({
                url: "/api/v1/files/",
                method: 'POST',
                body: fd,
                formData: true,
            }); },
        }),
        filesUploadCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/files/upload/",
                method: 'POST',
                body: queryArg,
            }); },
        }),
        filesUploadCompleteCreate: build.mutation({
            query: function (queryArg) { return ({
                url: "/api/v1/files/upload/".concat(queryArg.id, "/complete/"),
                method: 'POST',
            }); },
        }),
    }); },
    overrideExisting: false,
});
var useFilesCreateMutation = injectedRtkApi.useFilesCreateMutation, useFilesUploadCreateMutation = injectedRtkApi.useFilesUploadCreateMutation, useFilesUploadCompleteCreateMutation = injectedRtkApi.useFilesUploadCompleteCreateMutation;

var patchedApi = generatedApi.injectEndpoints({
    endpoints: function (build) { return ({
        crmApplicationDocumentUpload: build.mutation({
            query: function (fd) { return ({
                url: "/api/v1/crm/documents/",
                method: 'POST',
                body: fd,
                formData: true,
            }); },
        }),
        inspectionPhotoUpload: build.mutation({
            query: function (fd) { return ({
                url: "/api/v1/inspections/photos/",
                method: 'POST',
                body: fd,
                formData: true,
            }); },
        }),
        reportFileSign: build.mutation({
            query: function (fd) { return ({
                url: "/api/v1/reports/".concat(fd.get('id'), "/sign/"),
                method: 'POST',
                body: fd,
                formData: true,
            }); },
        }),
    }); },
});
var useCrmApplicationDocumentUploadMutation = patchedApi.useCrmApplicationDocumentUploadMutation, useInspectionPhotoUploadMutation = patchedApi.useInspectionPhotoUploadMutation, useReportFileSignMutation = patchedApi.useReportFileSignMutation;

export { injectedRtkApi$1 as api, baseQuery, generatedApi, injectedRtkApi, useApiV1AppraisersEvaluatingCompaniesListQuery, useApiV1AppraisersListQuery, useApiV1AppraisersRetrieveQuery, useApiV1BanksListQuery, useApiV1BanksRetrieveQuery, useApiV1BuildingsHouseRetrieveQuery, useApiV1BuildingsSearchRetrieveQuery, useApiV1CitiesListQuery, useApiV1CitiesRetrieveQuery, useApiV1CrmAssessmentObjectCreateMutation, useApiV1CrmAssessmentObjectDestroyMutation, useApiV1CrmAssessmentObjectListQuery, useApiV1CrmAssessmentObjectPartialUpdateMutation, useApiV1CrmAssessmentObjectRetrieveQuery, useApiV1CrmAssessmentObjectUpdateMutation, useApiV1CrmCustomerOrdersListQuery, useApiV1CrmCustomerOrdersPartialUpdateMutation, useApiV1CrmCustomerOrdersRetrieveQuery, useApiV1CrmCustomerOrdersUpdateMutation, useApiV1CrmCustomersCreateMutation, useApiV1CrmCustomersDestroyMutation, useApiV1CrmCustomersListQuery, useApiV1CrmCustomersPartialUpdateMutation, useApiV1CrmCustomersRetrieveQuery, useApiV1CrmCustomersUpdateMutation, useApiV1CrmDocumentsCreateMutation, useApiV1CrmDocumentsDestroyMutation, useApiV1CrmDocumentsGroupsCreateMutation, useApiV1CrmDocumentsGroupsDestroyMutation, useApiV1CrmDocumentsGroupsListQuery, useApiV1CrmDocumentsGroupsPartialUpdateMutation, useApiV1CrmDocumentsGroupsRetrieveQuery, useApiV1CrmDocumentsGroupsUpdateMutation, useApiV1CrmDocumentsListQuery, useApiV1CrmDocumentsPartialUpdateMutation, useApiV1CrmDocumentsRetrieveQuery, useApiV1CrmDocumentsUpdateMutation, useApiV1CrmOrdersCreateMutation, useApiV1CrmOrdersDestroyMutation, useApiV1CrmOrdersListQuery, useApiV1CrmOrdersNextRetrieveQuery, useApiV1CrmOrdersOwnersCreateMutation, useApiV1CrmOrdersOwnersDestroyMutation, useApiV1CrmOrdersOwnersPartialUpdateMutation, useApiV1CrmOrdersOwnersUpdateMutation, useApiV1CrmOrdersPartialUpdateMutation, useApiV1CrmOrdersRetrieveQuery, useApiV1CrmOrdersSendCreateMutation, useApiV1CrmOrdersSignCreateMutation, useApiV1CrmOrdersStartCreateMutation, useApiV1CrmOrdersUpdateMutation, useApiV1InspectionsCreateMutation, useApiV1InspectionsDestroyMutation, useApiV1InspectionsListQuery, useApiV1InspectionsPartialUpdateMutation, useApiV1InspectionsPhotoGroupsCreateMutation, useApiV1InspectionsPhotoGroupsDestroyMutation, useApiV1InspectionsPhotoGroupsListQuery, useApiV1InspectionsPhotoGroupsPartialUpdateMutation, useApiV1InspectionsPhotoGroupsRetrieveQuery, useApiV1InspectionsPhotoGroupsUpdateMutation, useApiV1InspectionsPhotosCreateMutation, useApiV1InspectionsPhotosDestroyMutation, useApiV1InspectionsPhotosPartialUpdateMutation, useApiV1InspectionsPhotosUpdateMutation, useApiV1InspectionsRetrieveQuery, useApiV1InspectionsUpdateMutation, useApiV1PortalAddressAutocompleteRetrieveQuery, useApiV1PortalCustomerOrdersCreateMutation, useApiV1ReportsFilesCreateMutation, useApiV1ReportsFilesRetrieveQuery, useApiV1ReportsGenerateCreateMutation, useApiV1ReportsListQuery, useApiV1ReportsRefillCreateMutation, useApiV1ReportsRetrieveQuery, useApiV1ReportsSignCreateMutation, useApiV1ReportsTemplatesListQuery, useApiV1ResidentialApartmentsListQuery, useApiV1ResidentialApartmentsRetrieveQuery, useApiV1ResidentialBuildingsListQuery, useApiV1ResidentialBuildingsRetrieveQuery, useApiV1ResidentialComplexesListQuery, useApiV1ResidentialComplexesRetrieveQuery, useApiV1ResidentialDevelopersListQuery, useApiV1ResidentialDevelopersRetrieveQuery, useApiV1ResidentialEntrancesListQuery, useApiV1SelectionsCreateMutation, useApiV1SelectionsListQuery, useApiV1SelectionsRetrieveQuery, useApiV1UsersMeRetrieveQuery, useCrmApplicationDocumentUploadMutation, useFilesCreateMutation, useFilesUploadCompleteCreateMutation, useFilesUploadCreateMutation, useInspectionPhotoUploadMutation, useLazyApiV1AppraisersEvaluatingCompaniesListQuery, useLazyApiV1AppraisersListQuery, useLazyApiV1AppraisersRetrieveQuery, useLazyApiV1BanksListQuery, useLazyApiV1BanksRetrieveQuery, useLazyApiV1BuildingsHouseRetrieveQuery, useLazyApiV1BuildingsSearchRetrieveQuery, useLazyApiV1CitiesListQuery, useLazyApiV1CitiesRetrieveQuery, useLazyApiV1CrmAssessmentObjectListQuery, useLazyApiV1CrmAssessmentObjectRetrieveQuery, useLazyApiV1CrmCustomerOrdersListQuery, useLazyApiV1CrmCustomerOrdersRetrieveQuery, useLazyApiV1CrmCustomersListQuery, useLazyApiV1CrmCustomersRetrieveQuery, useLazyApiV1CrmDocumentsGroupsListQuery, useLazyApiV1CrmDocumentsGroupsRetrieveQuery, useLazyApiV1CrmDocumentsListQuery, useLazyApiV1CrmDocumentsRetrieveQuery, useLazyApiV1CrmOrdersListQuery, useLazyApiV1CrmOrdersNextRetrieveQuery, useLazyApiV1CrmOrdersRetrieveQuery, useLazyApiV1InspectionsListQuery, useLazyApiV1InspectionsPhotoGroupsListQuery, useLazyApiV1InspectionsPhotoGroupsRetrieveQuery, useLazyApiV1InspectionsRetrieveQuery, useLazyApiV1PortalAddressAutocompleteRetrieveQuery, useLazyApiV1ReportsFilesRetrieveQuery, useLazyApiV1ReportsListQuery, useLazyApiV1ReportsRetrieveQuery, useLazyApiV1ReportsTemplatesListQuery, useLazyApiV1ResidentialApartmentsListQuery, useLazyApiV1ResidentialApartmentsRetrieveQuery, useLazyApiV1ResidentialBuildingsListQuery, useLazyApiV1ResidentialBuildingsRetrieveQuery, useLazyApiV1ResidentialComplexesListQuery, useLazyApiV1ResidentialComplexesRetrieveQuery, useLazyApiV1ResidentialDevelopersListQuery, useLazyApiV1ResidentialDevelopersRetrieveQuery, useLazyApiV1ResidentialEntrancesListQuery, useLazyApiV1SelectionsListQuery, useLazyApiV1SelectionsRetrieveQuery, useLazyApiV1UsersMeRetrieveQuery, useReportFileSignMutation };
